// src/pages/nafed/pacs/PacsMouSigning.js
// Full PMC-PACS two-party MoU generator and final-execution workflow.

import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import { doc, getDoc, runTransaction, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";

const MAX_SIGNATURE_SIZE = 2 * 1024 * 1024;
export const MOU_TEMPLATE_VERSION = "PMC_PACS_TWO_PARTY_MOU_V11_AUTO_RECORDS_2026_09_16";
const SIGNING_ORDER = ["PMC", "PACS"];
const PRELIMINARY_EXPENSE_BASIS =
  "As per Accepted / Approved DPR and actual verified cost";

const NAFED_DEFAULTS = Object.freeze({
  legalName: "National Agricultural Cooperative Marketing Federation of India Ltd. (NAFED)",
  headOffice:
    "NAFED House, Sidhartha Enclave, Ring Road, Ashram Chowk, New Delhi - 110014",
});

const PMC_DEFAULTS = Object.freeze({
  legalName:
    "Shine Agri Allied and Marketing Multistate Cooperative Society Limited",
  registrationNumber: "MSCS/CR/1572/2024",
  address:
    "48, First Amar Arcade, Nilpur, Taluka Dantiwada, District Banaskantha, Gujarat - 385505",
});

const MOU_BLOCKS = [
  {
    "type": "heading",
    "text": "Parties and recitals",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "This Memorandum of Understanding (MoU), Version 11, is executed at {{executionPlace}} by the following Parties. Its effective date is the last authorized execution date recorded in the signature records, subject to Article 25.2.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "{{pmcName}}, Registration No. {{pmcRegistrationNumber}}, having its office at {{pmcAddress}}, acting through its authorized {{pmcSignatoryDesignation}}, {{pmcSignatoryName}}, is the First Party / PMC. NAFED appointment / allocation reference: {{pmcLoaNumber}} dated {{pmcLoaDate}}; copy to be attached.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "{{pacsName}}, Registration No. {{pacsRegistrationNumber}}, governed by {{pacsGoverningLaw}}, having its registered office at {{pacsAddress}}, acting through Chairperson {{chairpersonName}} and Secretary {{secretaryName}} under Resolution No. {{boardResolutionNumber}} dated {{boardResolutionDate}}, is the Second Party / PACS / Project Owner.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "The PACS proposes a {{capacityMt}} MT grain storage and associated infrastructure Project at {{location}}. This MoU records the PMC-PACS service framework and the PACS obligation to fund services and project commitments in advance. NAFED is not a signatory to this MoU. Its appointment, project allocation and instructions must be evidenced by the applicable written documents.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Interpretation priority",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "This MoU establishes percentage-based funding of the total Accepted DPR Project Amount through two separate payment routes. PMC service charges follow the NAFED SLA escrow/designated-account mechanism. Non-fee Project funds follow the PACS-to-PMC dedicated Project Collection Account-to-Contractor/vendor route only after the Schedule 5 conditions are fulfilled. The SLA does not itself specify or authorize that construction-fund route. This MoU cannot bind NAFED or a lender or override their applicable written conditions.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 1  DEFINITIONS AND INTERPRETATION",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.1 \"Accepted DPR\" means the Detailed Project Report, drawings, estimates, financing plan and schedule accepted by NAFED and/or the competent authority and acknowledged by the PACS.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.2 \"Applicable Law\" includes Central and State laws, cooperative laws, rules, codes, lender conditions, scheme guidelines, permits and binding governmental directions.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.3 \"Contractor\" means any contractor, vendor, supplier, consultant or agency engaged under a separate contract for execution or supply.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.4 \"Financial Closure\" means written confirmation of the complete funding plan, including loan, PACS margin, actually sanctioned grant/subsidy and other approved sources adequate to implement the Accepted DPR.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.5 \"Preliminary Works\" include document scrutiny, survey, soil/geotechnical investigation, layout, design, drawings, estimates, financial analysis and DPR preparation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.6 \"Preliminary Expense Budget\" means the itemized internal allocation for survey, testing, design, statutory and other admissible preliminary work. Such costs may be allocated separately from the PMC fee only where the controlling appointment and written approvals permit; no service, travel or incidental expense already included in the PMC fee is charged again.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.7 \"D\" means the total Accepted DPR Project Amount, funded by the approved financing plan and inclusive of civil works, machinery, approved preliminary expenses, the admissible PMC fee and budgeted taxes. All Schedule 2 percentages apply to D as a whole. Internal cost allocations do not create additional demands above D. Changes require an approved DPR variation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.8 \"PMC Fee\" means the admissible service-fee allocation within D under Article 9. \"Project Funding Instalments\" means five deposits of 30%, 20%, 20%, 20% and 10% of D. These deposits fund all approved cost heads together; they are not separate fee instalments.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.9 \"Fee Escrow Account\" means the escrow/designated account specified by NAFED for PMC Service Charges under SLA Article 10. \"Project Collection Account\" means the dedicated bank account in PMC legal name, identified for this PACS Project and authorized under Schedule 5 solely for non-fee Project funds and onward disbursements. \"Designated Account\" means the applicable account for the particular cost head, not a single common account. \"Advance Deposit\" means funds received before corresponding expenditure, subject to adjustment. \"Working Day\" excludes Sundays and applicable local public/bank holidays.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.10 Mandatory law and the applicable NAFED appointment/SLA and valid written directions prevail over this MoU. Subject thereto, this MoU, its Schedules, the Accepted DPR, approved drawings and separate contractor agreements govern their respective subject matter. A PACS resolution or bank mandate alone does not amend the NAFED-PMC SLA.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1.11 The total-price instalments finance all approved Project heads together. The Parties shall record opening balances and the agreed internal allocations before a revised demand; a separate cost ledger does not create a separate extra fee.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 2  PURPOSE, APPOINTMENT AND RELATIONSHIP",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "2.1 The PMC acts within its written NAFED appointment and project allocation. The PACS grants site access and cooperation reasonably required for those approved services. Any NAFED approval or confirmation referred to herein requires its separate written action.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "2.2 This MoU is a project implementation framework. It is not a loan, subsidy sanction, guarantee, partnership, joint venture, transfer of ownership or construction contract.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "2.3 The PMC acts as an independent professional consultant. No Party may bind another beyond written authority, and no Party becomes jointly or severally liable merely by signing this MoU.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "2.4 Construction/supply shall be performed by independent Contractors under separate tender/work-order documents. Unless separately contracted in writing, the PMC is not the EPC Contractor and is not responsible for construction means, labour, plant, site safety or Contractor defaults. This allocation between PMC and PACS does not reduce PMC duties to NAFED under its SLA or excuse PMC professional negligence.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 3  CONDITIONS PRECEDENT",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "3.1 Subject to existing NAFED duties and Article 20, the PACS shall provide the following before substantive Preliminary Works or new external commitments:",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Certified Board / General Body resolutions approving the Project, this MoU, borrowing, Escrow/payment-routing mandate and signatory authority.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Complete land-title, possession, encumbrance, mutation, land-use and access documents and a no-dispute declaration.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Registration, KYC, audited financial, tax, scheme and lender documents requested through the portal or in writing.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Safe and unrestricted site access for survey, testing, inspection and monitoring.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "The first Project Funding Instalment under Schedule 2, the PACS board authorization supporting Schedule 4, the approved DPR cost heads, verified Designated Account details and any required written collection or disbursement authority.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "3.2 A preliminary visit, survey, discussion or document review does not constitute final Project approval or waiver of payment/conditions. Work already completed or funded is credited in the opening reconciliation. A cheque, expected loan, proposed subsidy or unsigned payment instruction is not cleared funding.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 4  SCOPE OF PMC SERVICES",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "4.1 Subject to timely payment, access and cooperation, the PMC shall perform or coordinate:",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PACS identification, need/capacity assessment and document scrutiny.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Site reconnaissance, topographic/station survey and soil/geotechnical investigation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Concept/layout, structural analysis, discipline designs, drawings, BOQ, estimates and specifications.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Project cost, means of finance, projected financial statements, viability/DSCR and DPR preparation/revision.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Lender/authority clarification and Financial Closure facilitation without guaranteeing sanction or disbursement.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Tender/procurement support, bid review, Contractor recommendation and baseline scheduling.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Material/quality inspections, progress monitoring, measurement and RA/final bill recommendation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Testing, commissioning, handover, as-built records, completion and DLP/final closure support.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "4.2 Additional work requires a written scope, price, funding source, time impact, PACS approval and any required NAFED/lender approval before commitment. Once accepted, its approved cost is incorporated into the revised DPR and reconciled in the Project Funding Instalments. An item already covered by the PMC fee or approved DPR cannot be billed twice.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "4.3 PMC acts as the appointed professional consultant within its written authority. It does not become the EPC contractor, construction employer, borrower or guarantor merely by coordinating procurement, monitoring work or routing certified payments. PMC remains responsible for its own agreed professional services and non-delegable duties under the NAFED appointment and Applicable Law.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 5  NAFED INTERFACE",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "NAFED programme directions, formats, reviews, reporting controls and approvals apply only under its own written appointment and instructions. This Article describes that interface and creates no new NAFED undertaking.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Review/cause review of submissions and communicate approvals, queries or corrections.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Specify the PMC-fee escrow/designated account and consider the separate Project-fund routing confirmation in Schedule 5. No approval, payment guarantee or construction-fund routing obligation is imposed on NAFED by this two-party MoU.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Release of funds, certification and any onward contractor/vendor payment through PMC shall follow the applicable written NAFED/lender mandate and approved payment SOP. This MoU alone is not authority to receive or route all construction funds through PMC.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Exercise inspection, audit, suspension, substitution and other rights under the applicable appointment.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "5.1 NAFED is not the owner, borrower, lender, guarantor, insurer, construction employer or Contractor and does not guarantee title, approval, loan, subsidy, viability, cost, schedule, revenue or Contractor performance.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "5.2 NAFED review, approval, inspection, certification or payment routing is a governance function and does not transfer the legal responsibility of the PACS, designer, lender, authority or Contractor.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 6  ROLE AND RESPONSIBILITIES OF THE PMC",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Perform with reasonable professional skill, care and diligence using qualified personnel/specialists.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Maintain verifiable survey, test, design, estimate, inspection, measurement, certification and correspondence records.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Report material non-compliance, unsafe work, delay, quality deficiency or suspected fund misuse.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Avoid unauthorized commitments, disclose conflicts and protect confidential/project data.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Do not alter capacity, structure, specification, cost or funding plan without required written approval.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "6.1 The PMC may rely on PACS/Contractor/lender/authority/laboratory documents unless an error is reasonably apparent and is not liable for concealed conditions, forged information, latent defects or matters outside reasonable inspection.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "6.2 PMC certification is a professional assessment, not a guarantee of Contractor performance, waiver of latent defects, assumption of site safety or final statutory approval.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 7  RESPONSIBILITIES OF THE PACS",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "7.1 The PACS remains the Project Owner and shall at its sole cost and risk:",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Provide lawful, dispute-free possession/access, land, approach, utilities and working space.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Obtain internal resolutions, land-use/building/fire/environment/electricity and other Project Owner approvals.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Provide complete and accurate information and disclose litigation, encumbrance, internal dispute and management change.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Pay Preliminary Expenses, taxes, service fee, Project Cost, variations, escalation and statutory charges when due.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Arrange loan, margin and confirmed funds without treating an expected subsidy as available before actual receipt.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Deposit the percentage-based Project Funding Instalments calculated on the total DPR amount in the verified Designated Account. Fund approved variations and legitimate shortfalls after reconciliation; do not pay the same fee or cost again as a separate demand.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Execute/enforce Contractor agreements, securities, retention, liquidated damages, insurance, warranty and DLP remedies.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Maintain insurance, site security, local coordination and the completed facility and comply with lender/scheme end-use conditions.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "7.2 Authorized Chairperson and Secretary execute this MoU and payment mandates on behalf of the PACS under valid resolutions. Management change, election or internal disagreement does not by itself cancel PACS accrued obligations. Their signatures do not create personal guarantees or personal liability of officers or members.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "7.3 The PMC shall promptly record PACS delays in funding, decisions, information, access or signatures and seek corresponding time relief and documented approved additional costs. Relief against NAFED must be obtained under the controlling SLA; it does not arise automatically from this MoU.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 8  PRELIMINARY WORKS WITHIN THE DPR BUDGET",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.1 Preliminary work is financed from the first and subsequent Project Funding Instalments under Schedule 2. Only admissible expenses recorded in the approved DPR and supported by the applicable approvals and actual cost records may be paid. No separate preliminary allocation form is part of this MoU. No separate fixed preliminary lump sum, duplicate advance or charge for services already included in PMC fees is payable outside the total DPR funding schedule.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.2 Before substantive preliminary commitments, PACS shall fund Stage I, being 30% of D, under Schedule 2. Authorized preliminary expenses and the admissible PMC fee are paid or earmarked from Project funds under their respective approvals. If an approved early requirement exceeds available funds, an agreed early draw of a later instalment reduces that later demand rupee for rupee, with required NAFED/lender permission.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.3 The approved DPR must distinguish the PMC fee from any separately admissible preliminary cost. A DPR entry alone does not override the NAFED SLA prohibition on extra charges. Providers are paid against authorized commitments and supporting documents; included services cannot be charged through both a cost head and the PMC fee.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.4 If the Project is rejected, withdrawn, deferred or does not proceed, deposits shall be reconciled against certified earned PMC fee and authorized, verified expenses actually incurred or irrevocably committed, together with lawful taxes and reasonable evidenced cancellation charges. Refunds and recoveries follow Articles 10 and 20. An unperformed service is not fully earned merely because advance money was deposited.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.5 Expected loan or subsidy reimbursement does not excuse PACS payment of a valid scheduled instalment. An approved preliminary overrun must be covered by an approved reallocation within D or an approved DPR variation; it is not automatically an additional charge. PMC is not required to pre-finance unfunded external commitments, subject to its controlling contractual duties.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8.6 PMC shall provide an internal-cost reconciliation on preliminary-work completion. Unspent funds remain available for later approved Project stages and are not charged again. On Project termination or final closure, unused/uncommitted amounts are refunded or adjusted under Article 20 within the authorized route; records and deliverables are handed over as the SLA requires.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 9  TOTAL DPR AMOUNT AND INCLUDED PMC FEE",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "9.1 The binding Project funding base is D, being the total amount in the Accepted / Approved DPR, including the admissible PMC fee and approved preliminary expenses. No fixed Project amount is stated in this MoU. The applicable DPR approval reference and current approved cost statement shall be attached to each demand. Any change shall follow the approved DPR variation and reconciliation process.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "9.2 The PMC fee is the lower of 2% of the total Accepted DPR Project Cost or INR 5,00,000 under the NAFED SLA. It shall be identified within D and is not payable again above the five Project Funding Instalments. Approved preliminary expenses, civil works, machinery, budgeted taxes and other admissible heads form the remaining approved allocations within D.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "9.3 For this funding schedule the PMC fee ceiling includes GST and included travel/incidental expenses in accordance with SLA clause 8.5, unless duly varied in writing. Tax components shall be disclosed within the appropriate approved DPR cost heads. No blanket GST surcharge is added to D where its taxes are already included. Lawful TDS is deposited with timely credit evidence and reflected in reconciliation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "9.4 Non-fee Project funds held by PMC as the disclosed payment administrator for PACS shall be recorded as restricted funds payable/applied on behalf of PACS, separately from PMC service revenue. Actual contractual substance and applicable tax law govern accounting and tax treatment; this label does not establish a GST exemption or pure-agent entitlement. PMC shall not invoice the entire DPR amount as its own service merely because it receives funds for onward payment.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 10  TOTAL DPR PAYMENT MILESTONES",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.1 PACS shall fund D in five instalments of 30%, 20%, 20%, 20% and 10%. The cumulative targets are 30%, 50%, 70%, 90% and 100% of D. These percentages cover the entire approved Project cost, including PMC fee and approved preliminary expenses. No separate percentage-based fee demand or duplicate preliminary expense demand shall be added. The Stage I base may be the mutually recorded provisional DPR estimate, reconciled to Accepted D at Stage II.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.2 Each instalment is payable within three calendar days after both its Schedule 2 trigger and receipt of the supported invoice/demand, with verified account details and required authority. The demand states D, cumulative funding percentage, target amount, previous credits, statutory deductions and net balance. It shall be cleared before the next discretionary funded work stage, subject to Article 20. No instalment is due before its trigger; all prior deposits and valid TDS credits reduce the demand.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.3 Split routing: the PMC-fee component shall be deposited by PACS into the Fee Escrow Account; non-fee construction, vendor and admissible preliminary-cost components shall be deposited into the Project Collection Account after Schedule 5 activation. Each supported demand shall identify both accounts, head-wise allocation, prior credits and the combined net demand. Combined deposits across both routes shall not exceed the applicable cumulative Schedule 2 target. No transfer to the PMC general operating account is authorized. Service Charges shall not be directly collected or retained by PMC except under express NAFED written permission under SLA clause 10.2. Pending routing approval, PMC shall not demand receipt into an unapproved account; the Parties shall record an approved alternative route without duplicate funding.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.4 The admissible PMC fee shall be allocated within Stage I funding and deposited separately into the Fee Escrow Account, up to the lawful Stage I total; any balance shall be identified in the next approved funding allocation. This is part of D, not an additional demand. Fee earning and release remain subject to SLA milestones of 5%, 15%, 20%, 25%, 20%, 10% and 5%, including DLP, certification and deductions. Advance fee deposit is subject to NAFED written acceptance; collection authority alone does not accelerate fee release or make an unearned deposit PMC income.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.5 An overdue, undisputed and lawfully payable instalment balance carries simple interest at 12% per annum on outstanding principal after credits, to the extent legally recoverable. No interest is charged on genuinely disputed amounts pending resolution, duplicate demands or funds already duly deposited merely because their subsequent release is delayed.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.6 PACS shall raise specific documented objections within three calendar days of a demand. PMC shall respond within three calendar days; undisputed amounts remain due. No unrelated claim may be set off except where law or an enforceable order permits. Silence alone does not waive fraud, duplicate recovery, calculation errors or statutory rights.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10.7 PACS shall sign permitted bank and certified release instructions promptly. Whenever D is revised, the next demand shall equal the applicable cumulative percentage of revised D less all credited amounts, rather than adding a second charge for the same cost. If revised D is lower than total funds held, surplus is reconciled or refunded under the approved mandate.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 11  FUND CONTROL AND CONTRACTOR PAYMENTS",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.1 PACS shall arrange loan, margin and confirmed funds sufficient to meet Schedule 2. A proposed subsidy or undisbursed loan is not cleared funding. Any agreed substitute lender undertaking or modified drawdown must be approved in writing by the relevant parties and NAFED/lender where required; PACS remains responsible for an unfunded shortfall.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.2 Schedule 2 governs advance funding by PACS. Payments to contractors remain governed by separate work orders, certified quantities, quality records and lender conditions. Funding 100% before completion does not entitle a Contractor to receive 100% before doing the work or extinguish retention and defect remedies.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.3 PMC shall support each demand with the Schedule 2 stage record. Stages IV and V require certified cumulative utilization of 60% and 80% respectively of D on approved Project heads, supported by expenditure records, invoices and payment evidence. Only approved expenditure and duly earned/released admissible fee count; unspent deposits, mere earmarking, duplicate entries and unsupported advances do not count. Fund utilization is distinct from physical construction progress.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.4 PMC is not required to pre-finance payments or guarantee PACS, lender or subsidy funding. PACS remains Project Owner and contractual payer. Once cleared funds and all required approvals are available, PMC shall release authorized net payments promptly under the approved SOP. PMC may hold a disputed or unsupported portion with written reasons, while processing undisputed approved sums. This does not excuse PMC-caused delay, negligent certification or misuse of entrusted funds.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.5 The Project Collection Account shall be dedicated to this Project, without commingling with PMC operating funds or other PACS funds. No lending, investment, cross-project transfer, lien for PMC fees or unilateral appropriation is permitted. The bank mandate shall record permitted use and account controls; any bank set-off restriction requires bank acceptance. Interest earned and approved bank charges shall be separately disclosed and credited/allocated to the Project under the mandate. Restricted funds remain subject to accounting, audit and repayment duties.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.6 The PACS Chairperson and Secretary shall sign/forward a duly certified funding/payment instruction within two Working Days. No cash, bearer instrument, unverified account or unauthorized third-party payment is permitted, and any payee account change requires independent verification.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.7 The final 10% of D becomes due at certified 80% utilization of D, after the preceding applicable triggers. Final completion, handover or stability certification is not a precondition to this funding deposit. Thus 100% of D is funded before final completion; completion and stability documents remain mandatory when actually due. Approved DPR variations require reconciliation and funding of the revised balance before changed work.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.8 The signed payment SOP shall identify the two bank accounts, contracting owner and invoice addressee, tax deductor/TAN, deposit and return responsibilities, authorized approvers, payment limits, maker-checker controls, retention, refund authority and turnaround times. PMC service invoices shall be raised to PACS and submitted for SLA certification and escrow release. Project funding demands and receipts are separate from service invoices; applicable advance tax documents shall be issued when required. Account changes require independent bank verification and the same approvals as the original account.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.9 Within ten Working Days after each month-end PMC shall give PACS a reconciliation of both routes: opening balance, receipts, previous credits, fee deposits and releases, certified bills, advance recovery, taxes, retentions, interest/charges and closing balances, with UTRs and supporting records. On completion, termination or mandate withdrawal, reconcile within thirty calendar days and return undisputed unspent/uncommitted funds within fifteen Working Days thereafter through the approved route. Document committed liabilities and disputed balances separately; disputes shall not delay return of an undisputed surplus. No automatic forfeiture applies.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.10 Contractor/vendor billing cycle: work orders shall identify PACS as employer/buyer and PMC as its limited payment administrator, unless a separately approved contract expressly provides otherwise. Contractor/vendor raises the advance request or RA/final tax invoice to PACS, with copy to PMC, measurements/delivery evidence and quality records. PMC verifies rates, quantities, prior payments, advance recovery, statutory deductions and retention and issues a payment certificate. PACS authorized officers approve the payment under the SOP. PMC then transfers the net payable from cleared Project Collection Account funds directly to the verified payee and records the UTR. No approval is inferred from silence. Any contractor advance requires express work-order authority and stipulated security.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.11 Payment administration does not make PMC the EPC contractor, purchaser, employer of contractor labour or guarantor of contractor performance. Contractor remains responsible under its contract; PMC retains its own professional, certification, supervision and NAFED SLA duties. PACS shall ensure the contractor acknowledges the invoice and payment route in its work order. PMC shall not sign as principal buyer or accept a personal guarantee under this mandate.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.12 The collection mandate activates only after an authorized PACS board resolution, execution of this MoU and the authority records described in Schedule 5, verified bank mandate, competent NAFED written concurrence to the proposed split route and applicable lender acceptance are attached. These are agreed conditions of this MoU; the SLA does not itself state that construction funds must pass through NAFED or PMC. If the lender requires direct vendor disbursement, record that approved route and credit such payments against D; do not demand the same sum again. Pending activation, do not commence unfunded discretionary commitments; Article 20 safety and preservation duties continue.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "11.13 Revocation or replacement of the payment mandate shall be prospective, with written notice, bank-control updates and an orderly reconciliation and handover. No further discretionary disbursement shall be made after effective revocation; existing certified commitments shall be resolved under written instructions. PMC shall preserve records and promptly notify payment errors or suspected diversion. Its liability for its own breach, fraud, negligence or misapplication is not excluded by this mandate.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 12  PROCUREMENT AND CONTRACTOR RESPONSIBILITY",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "12.1 Procurement follows NAFED/lender/Applicable Law. Award is issued by the competent Project Owner/designated agency; PMC evaluation/recommendation is advisory/governance.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "12.2 Each Contractor remains solely responsible for execution methods, labour and labour-law compliance, plant, temporary works, material quality, safety, insurance, environmental compliance, taxes, delay, defects and damage.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "12.3 NAFED/PMC may inspect, reject non-conforming work, require tests/corrective action and recommend withholding/recovery without assuming Contractor responsibility.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "12.4 The PACS shall enforce Contractor securities, retention, damages, insurance, warranty and DLP remedies. Failure to enforce does not make NAFED/PMC liable. PMC obligations expressly undertaken to NAFED and its own professional liability remain unaffected.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "12.5 Unauthorized direct change/commitment by the PACS with a Contractor is solely for the PACS account and need not be certified or funded.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 13  DESIGN, APPROVALS AND CHANGE CONTROL",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "13.1 Execution shall proceed only against approved-for-construction drawings/specifications or other written authorization.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "13.2 The PACS obtains statutory approvals as Project Owner. The PMC supports submissions within scope but does not control authority decisions/time.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "13.3 Capacity, site, layout, structure, specification, BOQ, cost, financing or completion-date changes require a written Change Order stating technical, cost, funding, tax and time impact.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "13.4 Redesign or repeated work caused by changed requirements, inaccurate PACS data or authority/lender changes requires the approved variation process under Article 4.2. Its admissible cost is included in a revised DPR allocation and the remaining Project Funding Instalments; included services cannot be charged twice.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 14  IMPLEMENTATION PERIOD AND DELAYS",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "14.1 The indicative implementation period is as stated in the approved schedule, measured from the later of site handover, Financial Closure, approved drawings, Contractor mobilization and availability of funds.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "14.2 PMC shall promptly document external delays, give contractual notices and request extensions and approved cost adjustments. PACS acknowledges its own proven delay and shall cooperate with the request. Any extension, suspension or cost claim against NAFED remains governed by its SLA and written decisions.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "14.3 Contractor delay damages are administered under the separate work order. This MoU does not cancel or cap PMC delay damages independently arising under the NAFED-PMC SLA. Liability between PMC and PACS remains subject to Article 19 and Applicable Law.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "14.4 As between PACS and PMC, delay caused by PACS funding/access failures, an authority, lender or independent Contractor shall not by itself establish PMC default. PMC shall demonstrate causation, promptly notify and mitigate the delay and seek the required extension. Liability for PMC's own contributing breach and NAFED's separate SLA rights is preserved.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 15  QUALITY, COMPLETION AND DLP",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "15.1 Materials/workmanship shall comply with the Accepted DPR, approved drawings, BIS/NBC requirements, specifications, work order and manufacturer instructions.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "15.2 NAFED/PMC may require tests, samples, records, opening-up or retesting; non-compliance costs are borne by the responsible Contractor/PACS.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "15.3 Practical Completion requires contracted work completion except non-critical punch items, specified tests, required use clearances, as-built documents, warranties and handover records.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "15.4 Commissioning/use does not waive defects. The contractor DLP must be recorded in its work order. PMC duties to NAFED include the SLA DLP of 12 months, notified rectification timelines and applicable extensions. Advance funding does not waive DLP duties or permit premature release of the final 5% fee unless NAFED expressly agrees in writing.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 16  RECORDS, PORTAL, AUDIT AND INSPECTION",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "16.1 Project approvals, geo/time-stamped photographs, measurements, invoices, payment proofs, tests, correspondence and progress shall be maintained/uploaded in the approved system.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "16.2 NAFED, auditors, authorities and lender may inspect the site and relevant records. Originals/explanations shall be produced and audit observations cured within the stated time.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "16.3 Portal timestamps, digital approvals, verified electronic signatures and system audit logs are admissible project records subject to physical-original requirements.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 17  REPRESENTATIONS AND CONTINUING WARRANTIES",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "17.1 Each Party represents valid constitution, authority and no conflicting binding obligation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "17.2 The PACS continuously warrants valid resolutions/signatory authority; genuine sufficient land rights; true and complete information; Project-only fund use; disclosed related parties/conflicts; and compliance with cooperative law, bye-laws, lender and scheme.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "17.3 Representations repeat on each approval/payment/disbursement date. The PACS shall notify any material inaccuracy within two Working Days.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 18  NO GUARANTEE AND ALLOCATION OF RISK",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "18.1 Project approval, financing, market, price, ground-condition, construction, regulatory and operational risks remain with the Project Owner/relevant Contractor except for an express obligation.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "18.2 NAFED/PMC do not guarantee PACS/application/DPR approval, loan terms/disbursement, subsidy, cash flow, fixed cost/date, Contractor solvency/performance or storage income/utilization.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "18.3 Estimates use then-available scope, data and rates and may change for escalation, taxes, authority conditions, quantities and concealed conditions. PMC has no obligation to finance the Project from its own funds or provide security for PACS or Contractor debt unless separately and expressly agreed by duly authorized signatories.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "18.4 NAFED/PMC are not responsible for PACS internal/member/title dispute, lender/authority delay, Contractor default, theft, force majeure or misuse, except loss directly caused by their own fraud, wilful misconduct or gross negligence.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 19  INDEMNITY AND LIMITATION OF LIABILITY",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.1 The PACS shall indemnify NAFED/PMC and their officers against third-party claims/losses arising from disputed title/access, false PACS documents, PACS breach/non-payment/fund diversion, site/labour/property incident attributable to PACS/Contractor, or Contractor claim not caused by the indemnified Party's unauthorized commitment/fraud/gross negligence.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.2 As between PMC and PACS, PMC indemnifies PACS against finally established third-party claims directly caused by PMC fraud, wilful misconduct, gross negligence or original-deliverable infringement. Separate obligations to NAFED remain governed by the NAFED-PMC SLA and are not altered here.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.3 As between the Parties and subject to Applicable Law and express non-limitable obligations, neither is liable to the other for indirect or consequential loss, lost profit, opportunity or anticipated subsidy. This does not restrict restitution of unused advances, payment of valid accrued dues, or rights NAFED has under its separate SLA.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.4 Subject to Applicable Law, PMC aggregate contractual liability to PACS for this Project is limited to the admissible gross PMC fee for the Project, except fraud, wilful misconduct, proven negligent death/personal injury and liability that cannot lawfully be limited. Return of unused/unearned deposits, entrusted project funds and mandatory statutory dues is not restricted by this cap. The cap does not apply to independent NAFED claims under its SLA.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.5 Nothing in this two-party MoU imposes, varies or limits NAFED liability under a separate instrument. PACS funding obligations remain its own. Any indemnity payment requires evidence, causal attribution and reasonable mitigation; double recovery is prohibited.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "19.6 A Party seeking indemnity shall give prompt written notice and supporting records, allow reasonable participation in defence, mitigate loss and obtain the indemnifying Party's consent before a settlement binding it, such consent not to be unreasonably withheld. An amount becomes payable when agreed in writing or finally determined by the competent forum. No indemnity excuses the indemnified Party's own fraud, wilful misconduct or liability that cannot lawfully be excluded.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 20  PAYMENT DEFAULT SUSPENSION AND TERMINATION",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "20.1 For an overdue approved Schedule 2 instalment or another remediable PACS breach, PMC shall give written notice identifying the due trigger, calculation, previous credits and cure required. PACS has seven calendar days after receipt to cure or specify a good-faith dispute. PMC shall notify NAFED and obtain any direction/consent required before suspending SLA duties. New unfunded discretionary commitments may be deferred only as contractually permitted; urgent safety, preservation, reporting and statutory duties continue.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "20.2 If an undisputed material funding default continues for 15 calendar days after that notice, PMC may issue a further seven-calendar-day notice to terminate this PACS-facing arrangement, only to the extent permitted by its NAFED appointment and after any required NAFED consent/direction. Such termination does not by itself terminate the NAFED-PMC SLA. NAFED retains only the rights available under its own instruments.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "20.3 PACS withdrawal requires an authorized resolution and reconciliation of certified earned fee, authorized incurred/irrevocably committed expenses, certified project liabilities and reasonable evidenced cancellation costs, after all credits. The entire advance fee is not forfeited merely because PACS withdraws. Genuinely disputed sums are handled under Article 24; undisputed amounts remain payable.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "20.4 Within 30 days after termination/closure, PMC shall provide an itemized account, release undisputed refundable unearned/uncommitted amounts through the authorized route and identify any retained sum with its lawful basis. Pending third-party invoices are reconciled promptly when resolved. Handover, transition and record-access duties under the SLA continue irrespective of a payment claim; neither Party may use safety or statutory records as payment leverage.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 21  FORCE MAJEURE",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "21.1 A Party is excused to the extent performance is prevented by an event beyond reasonable control after prompt notice and mitigation. Time extends reasonably.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "21.2 Payment for completed services, accepted deliveries and committed third-party costs is not excused.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "21.3 After 90 consecutive days of substantial prevention, the Parties shall seek NAFED directions where required and may pursue permitted termination and Article 20 reconciliation. NAFED force-majeure powers remain as stated in its own SLA.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 22  CONFIDENTIALITY, DATA AND INTELLECTUAL PROPERTY",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "22.1 Parties shall protect non-public technical, financial, commercial, personal and security information and use it only for Project, approval, finance, audit and compliance.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "22.2 Disclosure is permitted to NAFED/government, lender, auditor, insurer, adviser and need-to-know Contractor under confidentiality, or as legally required.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "22.3 Pre-existing methods, templates and software remain with their owner except to the extent validly assigned. Project-specific DPRs, designs, records and deliverables shall vest and be handed over as required by Article 18 of the NAFED-PMC SLA. A payment dispute does not authorize withholding deliverables contrary to that SLA; the accrued recovery claim is preserved.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "22.4 The PACS shall not reuse a site design elsewhere, modify structural documents without competent approval or commercially distribute PMC materials without consent.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 23  COMPLIANCE, ETHICS, SAFETY AND INSURANCE",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "23.1 Parties comply with Applicable Law, anti-bribery, anti-money-laundering, tax and record rules. No facilitation payment, kickback or improper benefit is permitted.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "23.2 The PACS discloses related-party bidders/vendors. NAFED may exclude/re-tender, suspend payment or report suspected misconduct.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "23.3 Construction safety is the Contractor/Project Owner responsibility. PMC observation/stoppage recommendation does not make it occupier, employer or safety contractor.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "23.4 Before mobilization, the PACS ensures required contractor-all-risk, worker compensation, third-party, plant/equipment and other insurance.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 24  GOVERNING LAW AND DISPUTE RESOLUTION",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "24.1 This MoU is governed by Indian law, including the Arbitration and Conciliation Act, 1996 as amended, and applicable cooperative laws, subject to any mandatory statutory forum.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "24.2 The Parties shall exchange a detailed dispute notice and seek amicable resolution for 30 days through their authorized officers. NAFED may be requested to facilitate matters relating to its programme, without being made a contracting or arbitrating party merely by this MoU.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "24.3 If unresolved, the dispute goes to a mutually appointed sole arbitrator; failing agreement, appointment is sought under the Arbitration and Conciliation Act, 1996.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "24.4 Seat/legal place is New Delhi; hearings may be physical/virtual; language is English; the reasoned award is final and binding.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "24.5 Subject to a mandatory statutory forum, New Delhi courts have exclusive jurisdiction. Undisputed obligations/payments continue during dispute.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "ARTICLE 25  NOTICES AND MISCELLANEOUS",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.1 Notices are delivered by acknowledged hand, registered/speed post, recognized courier or registered email. Termination/arbitration notices require written acknowledgment where portal delivery is used.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.2 Upon fresh authorized execution, this Version 11 supersedes the earlier PMC-PACS MoU for this Project to the extent lawfully agreed, without extinguishing accrued rights, verified deposits or valid commitments. No revised term is backdated. All fee, preliminary or construction amounts previously received shall be reconciled and credited within Schedule 2 before a new demand.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.3 Amendments between PMC and PACS require their authorized written agreement and NAFED written approval wherever the change affects its appointment, fee, collection, releases or obligations. Nothing here grants PACS or PMC authority to amend the NAFED SLA without NAFED agreement. Assignment/substitution remains subject to the applicable appointment and lender requirements.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.4 Invalidity is limited to the minimum extent; remainder continues. Counterpart/electronic execution is permitted under Applicable Law.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.5 Applicable stamp duty and any required registration/notarization shall be determined for this instrument and place of execution before signing. PACS bears its execution costs unless law requires otherwise. Portal names and recorded signature images may be placed automatically only after authorized acceptance of this revision, with the actual signing date and audit record. An old signature record does not constitute acceptance of revised terms; portal images do not replace DSC/eSign or originals where required.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "25.6 Payment, audit, records, confidentiality, IP, indemnity, liability and dispute provisions survive completion/termination.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Schedule 1  Project particulars and prerequisites",
    "breakBefore": true
  },
  {
    "type": "table",
    "rows": [
      [
        "Particular",
        "Project entry"
      ],
      [
        "PACS",
        "{{pacsName}}"
      ],
      [
        "Project / capacity",
        "{{location}} / {{capacityMt}} MT"
      ],
      [
        "Application ID",
        "{{applicationId}}"
      ],
      [
        "PACS registration",
        "{{pacsRegistrationNumber}}"
      ],
      [
        "Total Project cost basis",
        "As per the Accepted / Approved DPR and its valid revisions; no fixed amount stated in this MoU"
      ],
      [
        "Land survey number",
        "{{landSurveyNumber}}"
      ],
      [
        "Land area as recorded",
        "{{landArea}}"
      ],
      [
        "Coordinates supplied",
        "{{latitude}} / {{longitude}}; to be verified against actual site survey before use"
      ],
      [
        "Existing PACS resolution",
        "No. {{boardResolutionNumber}} dated {{boardResolutionDate}}; confirm this authority expressly covers the current revision"
      ],
      [
        "NAFED LoA / project allocation",
        "Reference {{pmcLoaNumber}}  Date {{pmcLoaDate}}  Attach certified copy"
      ],
      [
        "DPR acceptance",
        "Reference {{dprApprovalReference}}  Date {{dprApprovalDate}}  Cost as per approved DPR"
      ],
      [
        "Implementation period",
        "{{projectDurationDays}} days"
      ],
      [
        "PMC DLP",
        "12 months under NAFED SLA, subject to notified extensions"
      ],
      [
        "Lender",
        "{{lender}}"
      ],
      [
        "Lender branch",
        "{{lenderBranch}}"
      ],
      [
        "Loan sanction reference",
        "{{loanSanctionReference}}"
      ],
      [
        "PMC signatory authority",
        "{{pmcAuthorityReference}} dated {{pmcAuthorityDate}}"
      ]
    ],
    "autoOnly": true
  },
  {
    "type": "paragraph",
    "text": "Project particulars above are populated from the available Project records. Omitted particulars do not constitute an approval, waiver or sanction. Required land, authority, budget and payment-route records remain subject to verification before the relevant commitment.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Schedule 2  Total DPR payment milestones",
    "breakBefore": true
  },
  {
    "type": "paragraph",
    "text": "D = total Accepted DPR Project Amount, including PMC fee, approved preliminary expenses, civil works, machinery and budgeted taxes. Every percentage below applies to this entire amount.",
    "breakBefore": false
  },
  {
    "type": "table",
    "rows": [
      [
        "Milestone and payment trigger",
        "Share of D",
        "Cumulative"
      ],
      [
        "I  Signing of MoU and verified collection mandate; mobilization advance against supported demand.",
        "30%",
        "30%"
      ],
      [
        "II  Submission and acceptance of DPR, drawings, designs and estimates; supported demand.",
        "20%",
        "50%"
      ],
      [
        "III  Selection and onboarding of Contractor under approved work order; supported demand.",
        "20%",
        "70%"
      ],
      [
        "IV  Certified utilization of 60% of D, with expenditure records and supported demand.",
        "20%",
        "90%"
      ],
      [
        "V  Certified utilization of 80% of D, with expenditure records and supported demand; before final completion.",
        "10%",
        "100%"
      ],
      [
        "TOTAL DPR FUNDING",
        "100%",
        "100%"
      ]
    ]
  },
  {
    "type": "heading",
    "text": "Payment within three calendar days",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Each instalment is due within three calendar days after both its trigger and the supported invoice/demand. Net demand = cumulative percentage x D, less all prior Project credits and valid TDS. Stage I may use the mutually recorded provisional estimate; Stage II reconciles to Accepted D. No numerical Project cost is fixed by this MoU.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "The final 10% deposit is due at certified 80% utilization of D. It does not await final completion, handover or stability certificates, which remain deliverable when actually due. Deposits do not authorize premature Contractor payments or override fee-release controls.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "PMC fee and preliminary expenses within D",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PMC fee: lower of 2% of D or INR 5,00,000, inclusive of GST under the supplied SLA. Preliminary expenses: as per the Accepted / Approved DPR and actual verified admissible cost. Both are included within D. No duplicate fee, expense or tax demand is payable. Fee release follows Article 10.4.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "D and its approved revisions are established by the accepted DPR record. Previous deposits, valid credits and disbursements are reconciled in the Project ledger before each demand. Acceptance of this funding schedule is recorded through the Parties' execution of this MoU; no separate handwritten amount or signature entry is required on this schedule.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Schedule 3  Deliverables and responsibility",
    "breakBefore": true
  },
  {
    "type": "table",
    "rows": [
      [
        "Workstream",
        "PMC deliverable",
        "Retained responsibility"
      ],
      [
        "Site scrutiny",
        "Document checklist, site note and registration",
        "PACS title and data accuracy"
      ],
      [
        "Survey and soil",
        "Survey drawings and geotechnical report",
        "Undiscoverable hidden conditions, subject to due care"
      ],
      [
        "Design and estimate",
        "Drawings, BOQ, estimates and specifications",
        "Authority decisions; approved changes"
      ],
      [
        "DPR and finance",
        "DPR and lender clarification support",
        "Loan/subsidy sanction and disbursement"
      ],
      [
        "Procurement",
        "Tender, evaluation and award support",
        "Contractor contractual performance"
      ],
      [
        "Construction monitoring",
        "Inspection, measurements, progress and bill certification",
        "Contractor execution, labour and safety; PMC retains its own SLA duties"
      ],
      [
        "Completion and DLP",
        "Commissioning, handover and defect monitoring",
        "PACS operation/maintenance; Contractor rectification; PMC SLA obligations continue"
      ]
    ]
  },
  {
    "type": "heading",
    "text": "Submission and acceptance",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PMC submits through the approved portal, email or physical process. PACS shall provide a consolidated specific review within ten Working Days, or the applicable notified period. NAFED review and approval follow its own procedures. Mandatory approvals and milestone certification cannot be deemed obtained merely by silence. In-scope comments are addressed within the agreed programme; additional work follows Article 4.2.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Evidence for advance funding and releases",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Each demand identifies the Project, payment head, budget/fee authority, supporting estimate, previous credits, net amount, due date and verified collection account. Each release carries the applicable certification, invoice/receipt and statutory deductions. Account changes require independent verification and any required fresh NAFED approval.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Schedule 4  PACS resolution and payment authorization",
    "breakBefore": true
  },
  {
    "type": "paragraph",
    "text": "For {{pacsName}}, the following payment authorizations are accepted by its authorized Chairperson and Secretary through execution of this MoU, subject to the actual board authority held in the Project records. This schedule is not a fabricated board resolution or a certification that a meeting occurred. A valid board resolution covering these authorizations remains required before the collection mandate becomes operative.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "1. Approve the {{capacityMt}} MT grain storage Project at {{village}} and Version 11 of this two-party PMC-PACS MoU, including all five Schedules, subject to applicable NAFED/lender approvals.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "2. Authorize Chairperson {{chairpersonName}} and Secretary {{secretaryName}} jointly to execute the revised MoU, budget approvals and lawful bank mandates on behalf of PACS.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "3. Approve five instalments of 30%, 20%, 20%, 20% and 10% of the entire Accepted DPR amount, payable within three calendar days under Schedule 2. The total includes the admissible PMC fee, approved preliminary expenses and all other approved Project heads; no separate duplicate demand is payable.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "4. Approve the internal preliminary budget, payable from the Project instalments. Do not authorize a duplicate fee or separate advance over D. Any early draw of a later instalment must reduce that later demand by the same amount.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "5. Approve cumulative funding of 30%, 50%, 70%, 90% and 100% of D, with final funding at certified 80% utilization of D before completion. Confirm the PMC fee within D is the lower of 2% of accepted Project cost or INR 5,00,000; approve preliminary expenses only as admissible and recorded in the DPR.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "6. Authorize PMC as limited Project-fund collection and disbursement administrator for PACS through the dedicated Project Collection Account, subject to Schedule 5 activation. Separately authorize deposit of PMC fees into the Fee Escrow Account. Authorize approved contractor payments with dual control and project-wise reconciliation; no blank cheque, personal guarantee or unrestricted debit is permitted.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "7. Authorize certified releases from earmarked advances under the controlling SLA and approved SOP without repeating a demand for money already deposited. Contractor funds, fee funds and preliminary-cost funds remain separately accounted for.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "8. Acknowledge that expected loan/subsidy reimbursement does not excuse an otherwise valid PACS funding obligation. Disputes must identify specific reasons; undisputed approved sums remain payable.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "9. Approve default notices, legally recoverable simple interest and reasonable evidenced recovery/cancellation costs as set out in the MoU, without automatic forfeiture of unearned advances.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "10. Confirm that management change or election does not cancel valid accrued PACS obligations. Authorize refunds and final reconciliation through the approved account route.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "The Chairperson and Secretary names, signatures and execution dates are supplied by the authorized signing workflow. The supporting board resolution is retained as a separate Project record; no handwritten certification block is required in this schedule.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "Schedule 5  Payment routing and authority records",
    "breakBefore": true
  },
  {
    "type": "paragraph",
    "text": "The Parties adopt the following payment-routing terms. Bank mandates, NAFED concurrence, PACS board authority and applicable lender acceptance are maintained as separate verifiable Project records. Signing this MoU or removing manual-entry fields does not create those approvals. No handwritten account, approval or signature form is required in this schedule.",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "Project: {{pacsName}} / {{applicationId}}. PMC: {{pmcName}}.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "1  Separate payment accounts",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PMC service charges shall be deposited into the NAFED-specified Fee Escrow / Designated Account. Non-fee Project funds shall be deposited into the dedicated PMC Project Collection Account only after the agreed authority conditions are fulfilled. Verified beneficiary bank details shall be supplied with the supported payment demand under the approved mandate; no account is designated merely by this generated MoU.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "2  Collection and contractor payment route",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PMC fee route: PACS to Fee Escrow / Designated Account to PMC under the SLA release milestones. Non-fee route: PACS to authorized PMC Project Collection Account to the verified Contractor/vendor after bill certification and PACS payment approval. The latter route requires the PACS board mandate, competent NAFED written concurrence, verified bank mandate and applicable lender acceptance described in Article 11.12. An approved direct lender/vendor route takes precedence where required, with full credit against the same DPR funding obligation.",
    "breakBefore": false
  },
  {
    "type": "heading",
    "text": "3  Fee release and evidence",
    "breakBefore": false
  },
  {
    "type": "paragraph",
    "text": "PMC fee release remains subject to the SLA milestones of 5%, 15%, 20%, 25%, 20%, 10% and 5%, including DLP/final closure, certification and statutory deductions. No accelerated release or SLA amendment is inferred from project advance collection. Authority records must be independently verified before use; their absence does not authorize collection. The approved payment SOP, project ledgers, supporting bills and bank records govern traceable disbursement, reconciliation and refunds under Articles 10 and 11.",
    "breakBefore": false
  }
];

const clean = (value, fallback = "Not available") =>
  String(value ?? "").trim() || fallback;

const upperText = (value = "") =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const isSupportedSignatureImage = (file) => {
  if (!file) return false;
  const mimeType = String(file.type || "").toLowerCase();
  const fileName = String(file.name || "");
  return (
    ["image/png", "image/jpeg", "image/jpg"].includes(mimeType) ||
    /\.(png|jpe?g)$/i.test(fileName)
  );
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const safeName = (value = "file") =>
  String(value)
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 100);

const numericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatInteger = (value) =>
  numericValue(value).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const formatMoney = (value, fallback = "To be finalized") => {
  const amount = numericValue(value);
  if (!amount) return fallback;
  return `INR ${amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value, fallback = "Not available") => {
  if (!value) return fallback;
  const date =
    value?.toDate instanceof Function ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return clean(value, fallback);
  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getFinancialYear = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const year = safeDate.getFullYear();
  const startYear = safeDate.getMonth() >= 3 ? year : year - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(new Error("Signature image could not be read."));
    reader.readAsDataURL(file);
  });

export const prepareSignatureDataUrl = async (dataUrl) => {
  if (
    !dataUrl ||
    typeof document === "undefined" ||
    typeof Image === "undefined"
  ) {
    return dataUrl || "";
  }

  const image = await new Promise((resolve, reject) => {
    const candidate = new Image();
    candidate.onload = () => resolve(candidate);
    candidate.onerror = () =>
      reject(new Error("Signature image could not be prepared."));
    candidate.src = dataUrl;
  });
  const source = document.createElement("canvas");
  source.width = Math.max(1, image.naturalWidth || image.width);
  source.height = Math.max(1, image.naturalHeight || image.height);
  const sourceContext = source.getContext("2d", { willReadFrequently: true });
  sourceContext.clearRect(0, 0, source.width, source.height);
  sourceContext.drawImage(image, 0, 0, source.width, source.height);

  const pixels = sourceContext.getImageData(
    0,
    0,
    source.width,
    source.height,
  ).data;
  let left = source.width;
  let top = source.height;
  let right = -1;
  let bottom = -1;

  for (let py = 0; py < source.height; py += 1) {
    for (let px = 0; px < source.width; px += 1) {
      const offset = (py * source.width + px) * 4;
      const alpha = pixels[offset + 3];
      const hasInk =
        alpha > 18 &&
        (pixels[offset] < 246 ||
          pixels[offset + 1] < 246 ||
          pixels[offset + 2] < 246);
      if (!hasInk) continue;
      left = Math.min(left, px);
      top = Math.min(top, py);
      right = Math.max(right, px);
      bottom = Math.max(bottom, py);
    }
  }

  if (right < left || bottom < top) return dataUrl;

  const detectedWidth = right - left + 1;
  const detectedHeight = bottom - top + 1;
  const padding = Math.max(
    5,
    Math.round(Math.max(detectedWidth, detectedHeight) * 0.025),
  );
  const cropLeft = Math.max(0, left - padding);
  const cropTop = Math.max(0, top - padding);
  const cropRight = Math.min(source.width - 1, right + padding);
  const cropBottom = Math.min(source.height - 1, bottom + padding);
  const cropWidth = cropRight - cropLeft + 1;
  const cropHeight = cropBottom - cropTop + 1;
  const scale = Math.min(1000 / cropWidth, 340 / cropHeight, 1);
  const target = document.createElement("canvas");
  target.width = Math.max(1, Math.round(cropWidth * scale));
  target.height = Math.max(1, Math.round(cropHeight * scale));
  const targetContext = target.getContext("2d");
  targetContext.clearRect(0, 0, target.width, target.height);
  targetContext.drawImage(
    source,
    cropLeft,
    cropTop,
    cropWidth,
    cropHeight,
    0,
    0,
    target.width,
    target.height,
  );
  return target.toDataURL("image/png");
};

// Embedded copies are bounded so three active signatures and history fit the record.
export const compactSignatureDataUrl = async (source) => {
  const prepared = await prepareSignatureDataUrl(source);
  if (prepared.length <= 80000) return prepared;
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Signature image is invalid. Choose a PNG/JPG file again."));
    image.src = prepared;
  });
  for (const width of [1000, 800, 600]) {
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, width / img.width, 340 / img.height);
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const context = canvas.getContext("2d");
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    const result = canvas.toDataURL("image/jpeg", 0.85);
    if (result.length <= 80000) return result;
  }
  throw new Error("Signature image is too complex. Choose a cropped PNG/JPG signature.");
};

export const urlToDataUrl = async (url) => {
  if (!url) return "";
  if (/^data:image\/(png|jpeg);base64,/i.test(url)) return compactSignatureDataUrl(url);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error("Saved signature download returned HTTP " + response.status + ". Choose the signature file again in the signing form.");
    return await compactSignatureDataUrl(await fileToDataUrl(await response.blob()));
  } catch (error) {
    if (error.name === "AbortError" || error instanceof TypeError) {
      throw new Error("Saved signature could not be downloaded (connection failed or timed out). Choose File to select this signer's PNG/JPG again, preview the MoU and accept it. Firebase must be reachable to upload and save.");
    }
    throw error;
  } finally { clearTimeout(timer); }
};

const safeUrlToDataUrl = async (url) => {
  if (!url) return "";
  try {
    return await urlToDataUrl(url);
  } catch (error) {
    console.warn("Existing signature could not be embedded in the PDF", error);
    return "";
  }
};

const sha256 = async (blob) => {
  if (!window.crypto?.subtle) return "";
  const buffer = await blob.arrayBuffer();
  const result = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(result))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const getPacsName = (profile = {}, project = {}) =>
  firstValue(
    project.societyName,
    project.pacsName,
    project.organizationName,
    profile.organizationName,
    profile.societyName,
    "PACS / Cooperative Society"
  );

const getApplicationId = (project = {}) =>
  firstValue(
    project.applicationId,
    project.projectId,
    project.id,
    "PACS"
  );

const getMouNumber = (project = {}) => {
  if (project.mouNumber) return project.mouNumber;
  const stateCode = safeName(
    firstValue(project.stateCode, project.state, "STATE")
  ).toUpperCase();
  return `NAFED/${stateCode}/${getFinancialYear(
    project.mouEffectiveDate || new Date()
  )}/${safeName(getApplicationId(project))}/MOU/V${Number(
    project.mouVersion || 1
  )}`;
};

const getPacsSignatureUrl = (pacsSignature = {}, role) => {
  const roleObject =
    role === "chairperson"
      ? pacsSignature.chairperson
      : pacsSignature.secretary;
  return firstValue(
    roleObject?.signatureUrl,
    role === "chairperson"
      ? pacsSignature.chairpersonSignatureUrl
      : pacsSignature.secretarySignatureUrl,
    role === "chairperson" ? pacsSignature.signatureUrl : ""
  );
};

export function buildMouData(profile = {}, project = {}) {
  // Freeze the PACS data source across PMC and PACS logins for this revision.
  profile = project.mouDocumentProfile || profile;
  const landLease = project.landLeaseDetails || {};
  const state = clean(firstValue(project.state, profile.state), "");
  const district = clean(firstValue(project.district, profile.district), "");
  const taluka = clean(
    firstValue(project.taluka, project.block, profile.taluka),
    ""
  );
  const village = clean(firstValue(project.village, profile.village), "");
  const capacityMt = numericValue(
    firstValue(
      project.proposedCapacityMt,
      project.capacityMt,
      project.capacity
    )
  );
  const pacsAddress = firstValue(
    project.registeredAddress,
    project.pacsAddress,
    project.address,
    profile.registeredAddress,
    profile.address
  );
  const projectCost = firstValue(
    project.acceptedDprCost,
    project.totalProjectCost,
    project.dprProjectCost,
    project.dprCost,
    project.estimatedProjectCost,
    project.projectCost
  );
  return {
    mouNumber: getMouNumber(project),
    templateVersion: MOU_TEMPLATE_VERSION,
    applicationId: getApplicationId(project),
    executionDate: firstValue(
      project.mouEffectiveDate,
      project.mouGeneratedAtIso,
      new Date().toISOString()
    ),
    executionPlace: firstValue(
      project.mouExecutionPlace,
      project.nafedBranchCity,
      project.branchCity,
      district,
      state
    ),
    stampValue: numericValue(
      firstValue(
        project.mouStampValue,
        project.stampPaperValue,
        project.eStampValue,
        300
      )
    ) || 300,
    pacsName: getPacsName(profile, project),
    pacsRegistrationNumber: firstValue(
      project.pacsRegistrationNumber,
      project.pacsRegistrationNo,
      project.registrationNumber,
      project.registrationNo,
      profile.pacsRegistrationNumber,
      profile.registrationNumber
    ),
    pacsGoverningLaw: firstValue(
      project.pacsGoverningLaw,
      state ? `${state} cooperative societies law and applicable rules` : ""
    ),
    pacsAddress,
    pacsEmail: firstValue(project.pacsEmail, profile.email),
    chairpersonName: firstValue(
      project.chairpersonName,
      project.chairmanName,
      profile.chairpersonName,
      profile.chairmanName
    ),
    secretaryName: firstValue(
      project.secretaryName,
      profile.secretaryName,
      profile.name
    ),
    boardResolutionNumber: firstValue(
      project.boardResolutionNumber,
      project.boardResolutionNo,
      project.resolutionNumber,
      project.resolutionNo,
      project.committeeResolutionNo,
      project.managingCommitteeResolutionNo
    ),
    boardResolutionDate: firstValue(
      project.boardResolutionDate,
      project.resolutionDate,
      project.committeeResolutionDate,
      project.managingCommitteeResolutionDate
    ),
    village,
    taluka,
    district,
    state,
    pinCode: firstValue(project.pinCode, project.pincode, profile.pinCode),
    location: [village, taluka, district, state].filter(Boolean).join(", "),
    capacityMt,
    landSurveyNumber: firstValue(
      landLease.surveyNo,
      project.landSurveyNumber,
      project.landSurveyNo,
      project.surveyNumber,
      project.surveyNo
    ),
    landArea: firstValue(
      landLease.landArea,
      project.landArea,
      project.landAreaSqMt,
      project.plotArea
    ),
    latitude: firstValue(
      project.latitude,
      project.siteLatitude,
      project.geoLocation?.lat
    ),
    longitude: firstValue(
      project.longitude,
      project.siteLongitude,
      project.geoLocation?.lng
    ),
    projectCost,
    projectCostText: formatMoney(projectCost),
    preliminaryExpenseBasisText: PRELIMINARY_EXPENSE_BASIS,
    projectDurationDays: firstValue(
      project.projectDurationDays,
      project.constructionDurationDays,
      project.workDurationDays,
      project.durationDays
    ),
    dlpPeriod: firstValue(
      project.dlpPeriod,
      project.defectLiabilityPeriod,
      project.dlpMonths
        ? `${project.dlpMonths} months`
        : ""
    ),
    lender: firstValue(
      project.lenderName,
      project.bankName,
      project.financingBank
    ),
    lenderBranch: firstValue(
      project.lenderBranch,
      project.bankBranch,
      project.financingBankBranch
    ),
    nafedLegalName: NAFED_DEFAULTS.legalName,
    nafedHeadOffice: NAFED_DEFAULTS.headOffice,
    nafedOffice: firstValue(
      project.nafedBranchName,
      project.branchName,
      state ? `NAFED ${state} State Branch` : "Concerned NAFED State Branch"
    ),
    nafedOfficeAddress: firstValue(
      project.nafedBranchAddress,
      project.branchAddress,
      NAFED_DEFAULTS.headOffice
    ),
    nafedSignatoryName: firstValue(
      project.nafedSignatoryName,
      project.mouSignatures?.branch?.name
    ),
    nafedSignatoryDesignation: firstValue(
      project.nafedSignatoryDesignation,
      project.mouSignatures?.branch?.designation
    ),
    pmcName: firstValue(
      project.pmcName,
      project.assignedPmcName,
      PMC_DEFAULTS.legalName
    ),
    pmcRegistrationNumber: firstValue(
      project.pmcRegistrationNumber,
      PMC_DEFAULTS.registrationNumber
    ),
    pmcAddress: firstValue(project.pmcAddress, PMC_DEFAULTS.address),
    pmcLoaNumber: firstValue(
      project.nafedPmcLoaNumber,
      project.pmcLoaNumber,
      project.pmcWorkOrderNumber,
      project.loaNumber
    ),
    pmcLoaDate: firstValue(
      project.nafedPmcLoaDate,
      project.pmcLoaDate,
      project.pmcWorkOrderDate,
      project.loaDate
    ),
    pmcSignatoryName: firstValue(
      project.pmcSignatoryName,
      project.mouSignatures?.pmc?.name
    ),
    pmcSignatoryDesignation: firstValue(
      project.pmcSignatoryDesignation,
      project.mouSignatures?.pmc?.designation
    ),
  };
}

export function getMissingMouFields(data) {
  const required = [
    ["PACS legal name", data.pacsName],
    ["PACS registration number", data.pacsRegistrationNumber],
    ["PACS registered address", data.pacsAddress],
    ["Chairperson name", data.chairpersonName],
    ["Secretary name", data.secretaryName],
    ["Board resolution number", data.boardResolutionNumber],
    ["Board resolution date", data.boardResolutionDate],
    ["Village", data.village],
    ["Taluka / Block", data.taluka],
    ["District", data.district],
    ["State", data.state],
    ["Storage capacity", data.capacityMt],
  ];
  return required
    .filter(([, value]) => !value || value === "Not available")
    .map(([label]) => label);
}

const PDF_THEME = Object.freeze({
  navy: [12, 48, 84],
  blue: [29, 91, 160],
  accent: [53, 119, 185],
  pale: [239, 245, 251],
  soft: [247, 250, 253],
  line: [174, 195, 216],
  ink: [30, 42, 55],
  muted: [86, 103, 121],
  white: [255, 255, 255],
  saffron: [229, 138, 36],
});

function addHeader(pdf, data, pageNumber, totalPages) {
  // The first page is a dedicated legal cover. Continuation pages intentionally
  // use only a small reference line, never the large repeated MOU masthead.
  if (pageNumber === 1) return;
  const width = pdf.internal.pageSize.getWidth();
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(5.8);
  pdf.text(`MOU No.: ${data.mouNumber}`, 40, 30);
  pdf.setFont("helvetica", "bold");
  pdf.text(`CONTINUATION SHEET | Page ${pageNumber} of ${totalPages}`, width - 40, 30, {
    align: "right",
  });
  pdf.setDrawColor(...PDF_THEME.line);
  pdf.setLineWidth(0.45);
  pdf.line(40, 40, width - 40, 40);
}

function addFooter(pdf, data, pageNumber, totalPages) {
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  pdf.setDrawColor(...PDF_THEME.line);
  pdf.line(40, height - 27, width - 40, height - 27);
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(5.4);
  pdf.text(data.applicationId, 40, height - 17);
  pdf.text(`MOU No.: ${data.mouNumber}`, width / 2, height - 17, {
    align: "center",
  });
  pdf.text(`Page ${pageNumber} of ${totalPages}`, width - 40, height - 17, {
    align: "right",
  });
}

function getImageType(dataUrl) {
  if (String(dataUrl).includes("image/png")) return "PNG";
  if (String(dataUrl).includes("image/webp")) return "WEBP";
  return "JPEG";
}

function addContainedImage(
  pdf,
  dataUrl,
  x,
  imageY,
  maxWidth,
  maxHeight,
  alias,
) {
  if (!dataUrl) return false;

  try {
    const properties = pdf.getImageProperties(dataUrl) || {};
    const naturalWidth = Number(properties.width || maxWidth);
    const naturalHeight = Number(properties.height || maxHeight);
    const scale = Math.min(
      maxWidth / Math.max(naturalWidth, 1),
      maxHeight / Math.max(naturalHeight, 1),
    );
    const drawWidth = Math.max(1, naturalWidth * scale);
    const drawHeight = Math.max(1, naturalHeight * scale);
    const drawX = x + (maxWidth - drawWidth) / 2;
    const drawY = imageY + (maxHeight - drawHeight) / 2;

    pdf.addImage(
      dataUrl,
      getImageType(dataUrl),
      drawX,
      drawY,
      drawWidth,
      drawHeight,
      alias,
      "FAST",
    );
    return true;
  } catch (error) {
    throw new Error("Signature image could not be fitted into the PDF. Reload or upload a valid image.");
  }
}

export function createMouPdf({
  profile = {},
  project = {},
  pacsSignatureDataUrl = "",
  pacsChairSignatureDataUrl = "",
  pacsSecretarySignatureDataUrl = "",
  pacsSigner = null,
  pmcSignatureDataUrl = "",
  pmcSigner = null,
}) {
  const data = buildMouData(profile, project);
  const pacsSignature = project.mouSignatures?.pacs || {};
  const pmcSignature = project.mouSignatures?.pmc || {};
  const resolvedPacsChairDataUrl =
    pacsChairSignatureDataUrl || pacsSignatureDataUrl;
  const resolvedPacsSecretaryDataUrl = pacsSecretarySignatureDataUrl;
  const fields = {
    ...data,
    pmcSignatoryName: firstValue(pmcSigner?.name, pmcSignature.name, data.pmcSignatoryName),
    pmcSignatoryDesignation: firstValue(pmcSigner?.designation, pmcSignature.designation, data.pmcSignatoryDesignation),
    chairpersonName: firstValue(pacsSigner?.chairperson?.name, data.chairpersonName),
    secretaryName: firstValue(pacsSigner?.secretary?.name, data.secretaryName),
    capacityMt: Number(data.capacityMt || 0).toLocaleString("en-IN"),
    boardResolutionDate: data.boardResolutionDate ? formatDate(data.boardResolutionDate) : "Not recorded",
    pmcLoaDate: data.pmcLoaDate ? formatDate(data.pmcLoaDate) : "Not recorded",
    dprApprovalReference: project.dprApprovalReference,
    dprApprovalDate: project.dprApprovalDate ? formatDate(project.dprApprovalDate) : "",
    baselineDate: project.baselineDate ? formatDate(project.baselineDate) : "",
    loanSanctionReference: project.loanSanctionReference,
    pmcAuthorityReference: project.pmcAuthorityReference,
    pmcAuthorityDate: project.pmcAuthorityDate ? formatDate(project.pmcAuthorityDate) : "",
  };
  const resolve = (s) => String(s || "").replace(/\{\{(\w+)\}\}/g, (_, key) => clean(fields[key], "Not recorded"));
  const printable = (s) => resolve(s).replace(/[\u2010-\u2015]/g,"-").replace(/[\u2018\u2019]/g,"'").replace(/[\u201c\u201d]/g,'"');
  const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = width - margin * 2;
  const topY = 80;
  // Reserve a permanent execution band so both Parties can initial/sign every page.
  const bottomY = height - 120;
  let y = topY;

  const addPage = () => {
    pdf.addPage();
    y = topY;
  };

  const ensureSpace = (required = 60) => {
    if (y + required <= bottomY) return false;
    addPage();
    return true;
  };

  const addRule = (color = PDF_THEME.blue) => {
    pdf.setDrawColor(...color);
    pdf.setLineWidth(0.7);
    pdf.line(margin, y, width - margin, y);
    y += 15;
  };

  const sectionHeading = (text, options = {}) => {
    const { pageBreak = false, center = false, size = 11.5 } = options;
    if (pageBreak && y > topY + 8) addPage();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(size);
    const headingLines = pdf.splitTextToSize(text, contentWidth);
    ensureSpace(headingLines.length * size * 1.25 + 65);
    pdf.setTextColor(...PDF_THEME.navy);
    pdf.text(headingLines, center ? width / 2 : margin, y, {
      align: center ? "center" : "left", lineHeightFactor: 1.25,
    });
    y += headingLines.length * size * 1.25 + 4;
    addRule();
  };

  const article = (number, title) => {
    // Keep an Article heading with at least its first substantive clause.
    ensureSpace(76);
    sectionHeading(`ARTICLE ${number} - ${title.toUpperCase()}`, { size: 10.8 });
  };

  const paragraph = (text, options = {}) => {
    const {
      size = 10.5,
      after = 9,
      indent = 0,
      bold = false,
      italic = false,
      align = "left",
      color = [35, 45, 51],
    } = options;
    const availableWidth = contentWidth - indent;
    pdf.setFont("helvetica", bold ? "bold" : italic ? "italic" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    const lines = pdf.splitTextToSize(clean(text), availableWidth);
    const lineHeight = size * 1.42;
    ensureSpace(lines.length * lineHeight + after);
    const textX =
      align === "center"
        ? width / 2
        : align === "right"
        ? width - margin
        : margin + indent;
    pdf.text(lines, textX, y, {
      align,
      maxWidth: availableWidth,
      lineHeightFactor: 1.42,
    });
    y += lines.length * lineHeight + after;
  };

  const clause = (number, text) => {
    const numberWidth = 36;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    const lines = pdf.splitTextToSize(text, contentWidth - numberWidth);
    const lineHeight = 14.6;
    ensureSpace(lines.length * lineHeight + 8);
    pdf.setTextColor(28, 40, 47);
    pdf.text(number, margin, y);
    pdf.text(lines, margin + numberWidth, y, {
      maxWidth: contentWidth - numberWidth,
      lineHeightFactor: 1.39,
    });
    y += lines.length * lineHeight + 8;
  };

  const bullets = (items) => {
    items.forEach((item) => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.7);
      const lines = pdf.splitTextToSize(item, contentWidth - 34);
      const lineHeight = 12;
      ensureSpace(lines.length * lineHeight + 6);
      pdf.setFillColor(...PDF_THEME.blue);
      pdf.circle(margin + 10, y - 2, 1.6, "F");
      pdf.setTextColor(32, 43, 49);
      pdf.text(lines, margin + 24, y, {
        maxWidth: contentWidth - 24,
        lineHeightFactor: 1.38,
      });
      y += lines.length * lineHeight + 6;
    });
    y += 2;
  };

  const callout = (title, text, fill = PDF_THEME.pale) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    const textLines = pdf.splitTextToSize(text, contentWidth - 22);
    const boxHeight = 31 + textLines.length * 11.4;
    ensureSpace(boxHeight + 8);
    pdf.setFillColor(...fill);
    pdf.setDrawColor(...PDF_THEME.accent);
    pdf.roundedRect(margin, y, contentWidth, boxHeight, 3, 3, "FD");
    pdf.setTextColor(...PDF_THEME.navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.8);
    pdf.text(title.toUpperCase(), margin + 10, y + 15);
    pdf.setTextColor(...PDF_THEME.ink);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.text(textLines, margin + 10, y + 29, {
      lineHeightFactor: 1.34,
    });
    y += boxHeight + 9;
  };

  const drawTable = ({
    headers,
    rows,
    widths,
    fontSize = 7.55,
    headerFill = PDF_THEME.navy,
  }) => {
    const paddingX = 5;
    const paddingY = 5;
    const lineHeight = fontSize * 1.28;
    const totalWidth = widths.reduce((sum, value) => sum + value, 0);
    if (Math.abs(totalWidth - contentWidth) > 1) {
      throw new Error("MOU table widths must equal the available page width.");
    }

    const calculateRow = (values, isHeader = false) => {
      pdf.setFont("helvetica", isHeader ? "bold" : "normal");
      pdf.setFontSize(fontSize);
      const wrapped = values.map((value, index) =>
        pdf.splitTextToSize(
          clean(value, ""),
          widths[index] - paddingX * 2
        )
      );
      const maxLines = Math.max(...wrapped.map((lines) => lines.length), 1);
      return {
        wrapped,
        height: Math.max(
          isHeader ? 24 : 22,
          maxLines * lineHeight + paddingY * 2
        ),
      };
    };

    const drawRow = (values, isHeader = false, shaded = false) => {
      const prepared = calculateRow(values, isHeader);
      if (y + prepared.height > bottomY) {
        addPage();
        drawRow(headers, true, false);
      }
      let x = margin;
      values.forEach((value, index) => {
        if (isHeader) {
          pdf.setFillColor(...headerFill);
        } else if (shaded) {
          pdf.setFillColor(...PDF_THEME.pale);
        } else {
          pdf.setFillColor(255, 255, 255);
        }
        pdf.setDrawColor(...PDF_THEME.line);
        pdf.rect(
          x,
          y,
          widths[index],
          prepared.height,
          isHeader || shaded ? "FD" : "D"
        );
        pdf.setTextColor(isHeader ? 255 : 35, isHeader ? 255 : 45, isHeader ? 255 : 50);
        pdf.setFont("helvetica", isHeader || shaded ? "bold" : "normal");
        pdf.setFontSize(fontSize);
        const centerColumn =
          index === 0 &&
          widths[index] <= 70;
        pdf.text(
          prepared.wrapped[index],
          centerColumn ? x + widths[index] / 2 : x + paddingX,
          y + paddingY + fontSize,
          {
            align: centerColumn ? "center" : "left",
            lineHeightFactor: 1.28,
          }
        );
        x += widths[index];
      });
      y += prepared.height;
    };

    ensureSpace(55);
    drawRow(headers, true, false);
    rows.forEach((row, index) =>
      drawRow(row, false, index === rows.length - 1 && row.__total === true)
    );
    y += 10;
  };

  const drawSignatureBox = ({
    label,
    name,
    designation,
    signatureDataUrl,
    status,
    signedAt,
    x,
    boxY,
    boxWidth,
    boxHeight = 150,
  }) => {
    pdf.setDrawColor(...PDF_THEME.line);
    pdf.setFillColor(...PDF_THEME.soft);
    pdf.roundedRect(x, boxY, boxWidth, boxHeight, 3, 3, "FD");
    pdf.setFillColor(...PDF_THEME.pale);
    pdf.rect(x, boxY, boxWidth, 23, "F");
    pdf.setTextColor(...PDF_THEME.navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.6);
    pdf.text(label, x + boxWidth / 2, boxY + 15, { align: "center" });

    if (signatureDataUrl) {
      addContainedImage(
        pdf,
        signatureDataUrl,
        x + 13,
        boxY + 28,
        boxWidth - 26,
        50,
      );
    } else {
      pdf.setTextColor(121, 132, 126);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.9);
      pdf.text(
        status === "SIGNED" ? "Portal signature recorded" : "Signature pending",
        x + boxWidth / 2,
        boxY + 57,
        { align: "center" }
      );
    }

    pdf.setDrawColor(...PDF_THEME.line);
    pdf.line(x + 10, boxY + 83, x + boxWidth - 10, boxY + 83);
    pdf.setTextColor(...PDF_THEME.ink);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.1);
    const nameLines = pdf
      .splitTextToSize(clean(name, "Authorised Signatory"), boxWidth - 18)
      .slice(0, 2);
    pdf.text(nameLines, x + boxWidth / 2, boxY + 96, {
      align: "center",
      lineHeightFactor: 1.15,
    });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.4);
    const designationLines = pdf.splitTextToSize(
      clean(designation, "Designation pending"),
      boxWidth - 18
    ).slice(0, 2);
    pdf.text(designationLines, x + boxWidth / 2, boxY + 119, {
      align: "center",
      lineHeightFactor: 1.12,
    });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.2);
    pdf.setTextColor(
      ...(status === "SIGNED" ? PDF_THEME.blue : [145, 92, 37])
    );
    pdf.text(
      status === "SIGNED"
        ? `SIGNED${signedAt ? ` - ${formatDate(signedAt)}` : ""}`
        : "PENDING",
      x + boxWidth / 2,
      boxY + 141,
      { align: "center" }
    );
  };

  const addLegalPageFrame = () => {
    pdf.setFillColor(...PDF_THEME.white);
    pdf.setDrawColor(...PDF_THEME.navy);
    pdf.setLineWidth(0.9);
    pdf.rect(20, 8, width - 40, height - 16, "D");
    pdf.setDrawColor(...PDF_THEME.line);
    pdf.setLineWidth(0.35);
    pdf.rect(24, 12, width - 48, height - 24, "D");
  };

  const addMiniSignatureImage = (dataUrl, x, imageY, imageWidth, alias) => {
    return addContainedImage(
      pdf,
      dataUrl,
      x,
      imageY,
      imageWidth,
      24,
      alias,
    );
  };

  const addPageExecutionBand = () => {
    const bandY = height - 105;
    const bandHeight = 70;
    const halfWidth = contentWidth / 2;
    const leftX = margin;
    const rightX = margin + halfWidth;

    pdf.setFillColor(...PDF_THEME.white);
    pdf.setDrawColor(...PDF_THEME.navy);
    pdf.setLineWidth(0.55);
    pdf.rect(margin, bandY, contentWidth, bandHeight, "FD");
    pdf.line(rightX, bandY, rightX, bandY + bandHeight);

    pdf.setTextColor(...PDF_THEME.navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.2);
    pdf.text("FIRST PARTY - PMC | SIGNATURE / INITIAL", leftX + 7, bandY + 11);
    pdf.text("SECOND PARTY - PACS | JOINT SIGNATURE / INITIAL", rightX + 7, bandY + 11);

    const pmcImageAdded = addMiniSignatureImage(
      pmcSignatureDataUrl,
      leftX + 8,
      bandY + 15,
      64,
      "PMC_EVERY_PAGE_SIGNATURE"
    );
    if (!pmcImageAdded) {
      pdf.setDrawColor(...PDF_THEME.muted);
      pdf.line(leftX + 8, bandY + 40, leftX + 72, bandY + 40);
    }
    pdf.setTextColor(...PDF_THEME.ink);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.8);
    pdf.text(
      clean(pmcSigner?.name || pmcSignature.name || data.pmcSignatoryName, "Authorised Signatory"),
      leftX + 82,
      bandY + 28,
      { maxWidth: halfWidth - 90 }
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.4);
    pdf.text(
      clean(pmcSigner?.designation || pmcSignature.designation || data.pmcSignatoryDesignation, "For the PMC"),
      leftX + 82,
      bandY + 41,
      { maxWidth: halfWidth - 90 }
    );

    const pacsHalf = halfWidth / 2;
    const chairX = rightX + 7;
    const secretaryX = rightX + pacsHalf + 3;
    const chairImageAdded = addMiniSignatureImage(
      resolvedPacsChairDataUrl,
      chairX,
      bandY + 15,
      52,
      "PACS_CHAIR_EVERY_PAGE_SIGNATURE"
    );
    const secretaryImageAdded = addMiniSignatureImage(
      resolvedPacsSecretaryDataUrl,
      secretaryX,
      bandY + 15,
      52,
      "PACS_SECRETARY_EVERY_PAGE_SIGNATURE"
    );
    if (!chairImageAdded) {
      pdf.setDrawColor(...PDF_THEME.muted);
      pdf.line(chairX, bandY + 40, chairX + 52, bandY + 40);
    }
    if (!secretaryImageAdded) {
      pdf.setDrawColor(...PDF_THEME.muted);
      pdf.line(secretaryX, bandY + 40, secretaryX + 52, bandY + 40);
    }
    pdf.setTextColor(...PDF_THEME.ink);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.15);
    pdf.text(
      clean(
        pacsSigner?.chairperson?.name ||
          pacsSignature.chairperson?.name ||
          data.chairpersonName,
        "Chairperson"
      ),
      chairX,
      bandY + 52,
      { maxWidth: pacsHalf - 12 }
    );
    pdf.text(
      clean(
        pacsSigner?.secretary?.name ||
          pacsSignature.secretary?.name ||
          data.secretaryName,
        "Secretary"
      ),
      secretaryX,
      bandY + 52,
      { maxWidth: pacsHalf - 12 }
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(4.9);
    pdf.text("Chairperson", chairX, bandY + 63);
    pdf.text("Secretary", secretaryX, bandY + 63);
  };

  // Cover page
  y = 86;
  pdf.setDrawColor(...PDF_THEME.navy);
  pdf.setLineWidth(1.1);
  pdf.line(margin + 68, y - 17, width - margin - 68, y - 17);
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("WORLD'S LARGEST GRAIN STORAGE PLAN IN COOPERATIVE SECTOR", width / 2, y, {
    align: "center",
  });
  y += 24;
  pdf.setTextColor(...PDF_THEME.ink);
  pdf.setFontSize(21);
  pdf.text("MEMORANDUM OF UNDERSTANDING", width / 2, y, {
    align: "center",
  });
  y += 17;
  pdf.setTextColor(...PDF_THEME.blue);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.6);
  pdf.text("APPOINTED PMC | PACS / PROJECT OWNER", width / 2, y, { align: "center" });
  y += 12;
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.6);
  pdf.text("Development of Grain Storage Infrastructure for PACS through NAFED", width / 2, y, {
    align: "center",
  });
  y += 14;
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(7.2);
  pdf.text(
    `To be executed on non-judicial stamp paper / e-stamp of Rs. ${formatInteger(
      data.stampValue
    )} or the value required by Applicable Law`,
    width / 2,
    y,
    { align: "center" }
  );
  y += 23;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13.5);
  // Measure and draw at the same font size; legal names wrap within the card.
  let coverTitleSize = 13.5;
  let pacsTitleLines = pdf.splitTextToSize(clean(data.pacsName), contentWidth - 54, {fontSize:coverTitleSize});
  while (pacsTitleLines.length > 4 && coverTitleSize > 10) {
    coverTitleSize -= 0.5; pdf.setFontSize(coverTitleSize);
    pacsTitleLines = pdf.splitTextToSize(clean(data.pacsName), contentWidth - 54, {fontSize:coverTitleSize});
  }
  const coverTitleLeading = coverTitleSize * 1.2;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.2);
  const siteLines = pdf.splitTextToSize(
    `Project Site: ${clean(data.location)}`,
    contentWidth - 54
  );
  const coverCardHeight = Math.max(
    170,
    130 + pacsTitleLines.length * coverTitleLeading + siteLines.length * 11
  );
  pdf.setFillColor(...PDF_THEME.soft);
  pdf.setDrawColor(...PDF_THEME.line);
  pdf.roundedRect(margin, y, contentWidth, coverCardHeight, 5, 5, "FD");
  pdf.setFillColor(...PDF_THEME.navy);
  pdf.roundedRect(margin, y, 6, coverCardHeight, 3, 3, "F");
  pdf.setTextColor(...PDF_THEME.blue);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.2);
  pdf.text("OFFICIAL PROJECT DOCUMENT", width / 2, y + 22, {
    align: "center",
  });
  pdf.setTextColor(...PDF_THEME.ink);
  pdf.setFontSize(coverTitleSize);
  pdf.text(pacsTitleLines, width / 2, y + 48, {
    align: "center",
    lineHeightFactor: 1.2,
  });
  const detailY = y + 63 + pacsTitleLines.length * coverTitleLeading;
  pdf.setTextColor(...PDF_THEME.navy);
  pdf.setFontSize(9.5);
  pdf.text(`${formatInteger(data.capacityMt)} MT GRAIN STORAGE PROJECT`, width / 2, detailY, {
    align: "center",
  });
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.2);
  pdf.text(siteLines, width / 2, detailY + 17, {
    align: "center",
    lineHeightFactor: 1.25,
  });
  pdf.setDrawColor(...PDF_THEME.line);
  const referenceY = y + coverCardHeight - 29;
  pdf.line(margin + 24, referenceY - 9, width - margin - 24, referenceY - 9);
  pdf.setFontSize(7.2);
  pdf.text(`MOU No.: ${data.mouNumber}`, margin + 24, referenceY + 4);
  pdf.text(`Application ID: ${data.applicationId}`, width - margin - 24, referenceY + 4, {
    align: "right",
  });
  y += coverCardHeight + 14;

  paragraph("BETWEEN", {
    bold: true,
    align: "center",
    color: PDF_THEME.muted,
    size: 9.6,
    after: 7,
  });

  const coverPartyGap = 10;
  const coverPartyWidth = (contentWidth - coverPartyGap) / 2;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  const pmcCoverName = pdf.splitTextToSize(data.pmcName, coverPartyWidth - 24);
  const pacsCoverName = pdf.splitTextToSize(data.pacsName, coverPartyWidth - 24);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6.9);
  const pmcCoverAddress = pdf.splitTextToSize(
    clean(data.pmcAddress),
    coverPartyWidth - 24,
  );
  const pacsCoverAddress = pdf.splitTextToSize(
    clean(data.pacsAddress),
    coverPartyWidth - 24,
  );
  const coverPartyHeight = Math.max(
    118,
    92 +
      Math.max(pmcCoverName.length, pacsCoverName.length) * 10.2 +
      Math.max(pmcCoverAddress.length, pacsCoverAddress.length) * 8.3,
  );

  const drawCoverParty = ({
    x,
    label,
    role,
    nameLines,
    registrationNumber,
    addressLines,
  }) => {
    pdf.setFillColor(...PDF_THEME.white);
    pdf.setDrawColor(...PDF_THEME.line);
    pdf.roundedRect(x, y, coverPartyWidth, coverPartyHeight, 3, 3, "FD");
    pdf.setFillColor(...PDF_THEME.navy);
    pdf.rect(x, y, 5, coverPartyHeight, "F");
    pdf.setTextColor(...PDF_THEME.blue);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.2);
    pdf.text(label, x + 13, y + 16);
    pdf.setTextColor(...PDF_THEME.ink);
    pdf.setFontSize(9);
    pdf.text(nameLines, x + 13, y + 34, {
      maxWidth: coverPartyWidth - 24,
      lineHeightFactor: 1.14,
    });
    let partyY = y + 38 + nameLines.length * 10.2;
    pdf.setTextColor(...PDF_THEME.navy);
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(7.1);
    pdf.text(role, x + 13, partyY);
    partyY += 13;
    pdf.setTextColor(...PDF_THEME.muted);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.8);
    pdf.text(`Registration No.: ${clean(registrationNumber)}`, x + 13, partyY);
    partyY += 12;
    pdf.setFontSize(6.9);
    pdf.text(addressLines, x + 13, partyY, {
      maxWidth: coverPartyWidth - 24,
      lineHeightFactor: 1.2,
    });
  };

  drawCoverParty({
    x: margin,
    label: "FIRST PARTY",
    role: "Project Management Consultant (PMC)",
    nameLines: pmcCoverName,
    registrationNumber: data.pmcRegistrationNumber,
    addressLines: pmcCoverAddress,
  });
  drawCoverParty({
    x: margin + coverPartyWidth + coverPartyGap,
    label: "SECOND PARTY",
    role: "PACS / Project Owner",
    nameLines: pacsCoverName,
    registrationNumber: data.pacsRegistrationNumber,
    addressLines: pacsCoverAddress,
  });
  y += coverPartyHeight + 11;

  const executionBoxHeight = 48;
  pdf.setFillColor(...PDF_THEME.pale);
  pdf.setDrawColor(...PDF_THEME.accent);
  pdf.roundedRect(margin, y, contentWidth, executionBoxHeight, 3, 3, "FD");
  pdf.setTextColor(...PDF_THEME.navy);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.2);
  pdf.text("EXECUTION DATE", margin + 12, y + 15);
  pdf.text("EXECUTION PLACE", margin + contentWidth / 3 + 12, y + 15);
  pdf.text("STAMP PAPER", margin + (contentWidth * 2) / 3 + 12, y + 15);
  pdf.setTextColor(...PDF_THEME.ink);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.2);
  pdf.text(project.mouFullyExecutedAtIso ? formatDate(project.mouFullyExecutedAtIso) : "On last authorized signature", margin + 12, y + 33, { maxWidth: contentWidth / 3 - 22 });
  pdf.text(clean(data.executionPlace), margin + contentWidth / 3 + 12, y + 33, {
    maxWidth: contentWidth / 3 - 22,
  });
  pdf.text(
    `Rs. ${formatInteger(data.stampValue)} / as applicable`,
    margin + (contentWidth * 2) / 3 + 12,
    y + 33,
    { maxWidth: contentWidth / 3 - 22 },
  );
  y += executionBoxHeight + 9;

  paragraph(
    "This cover page forms an integral part of the MoU. The Parties shall read it together with all Articles, Schedules, execution blocks and page-wise initials/signatures.",
    {
      italic: true,
      align: "center",
      size: 7.5,
      after: 0,
      color: PDF_THEME.muted,
    },
  );

  addPage();
  MOU_BLOCKS.forEach((block) => {
    if (block.type === "heading") {
      sectionHeading(printable(block.text), { pageBreak: block.breakBefore, size: 11.5 });
    } else if (block.type === "table") {
      const populated = value => value !== undefined && value !== null && String(value).trim() !== "" && !["Not recorded", "Not available"].includes(String(value));
      const sourceRows = block.autoOnly ? block.rows.filter((row, index) => index === 0 || row.every(cell => {
        const keys = [...String(cell).matchAll(/\{\{(\w+)\}\}/g)].map(match => match[1]);
        return !/_{3,}/.test(String(cell)) && keys.every(key => populated(fields[key]));
      })) : block.rows;
      const rows = sourceRows.map(row => row.map(printable));
      const n = rows[0].length;
      const fractions = n === 3 ? [0.54, 0.23, 0.23] : n === 2 ? [0.35, 0.65] : Array(n).fill(1 / n);
      drawTable({ headers: rows[0], rows: rows.slice(1), widths: fractions.map(f => f * contentWidth), fontSize: 9 });
    } else {
      const text = printable(block.text);
      const numbered = text.match(/^(\d+\.\d+)\s+(.*)$/s);
      if (block.text.startsWith("{{pmcName}}, Registration") || block.text.startsWith("{{pacsName}}, Registration")) {
        callout(block.text.startsWith("{{pmcName}}") ? "FIRST PARTY / PMC" : "SECOND PARTY / PACS", text);
      } else if (numbered) clause(numbered[1], numbered[2]);
      else paragraph(text);
    }
  });

  sectionHeading("EXECUTION AND SIGNATURES", {
    pageBreak: true,
    center: true,
    size: 13,
  });
  paragraph(
    "The authorized representatives confirm that they have read, understood and accepted this Version 11 MoU and Schedules 1 to 5 with fresh execution. Required payment-route approvals remain separate. Electronic/portal records do not replace DSC/eSign, stamp or physical execution where Applicable Law or NAFED requires them."
  );

  const signatureGap = 8;
  const threeBoxWidth = (contentWidth - signatureGap * 2) / 3;
  ensureSpace(166);
  const firstRowY = y;
  drawSignatureBox({
    label: "FIRST PARTY - PMC",
    name: pmcSigner?.name || pmcSignature.name || data.pmcSignatoryName,
    designation:
      pmcSigner?.designation ||
      pmcSignature.designation ||
      data.pmcSignatoryDesignation,
    signatureDataUrl: pmcSignatureDataUrl,
    status: pmcSignatureDataUrl ? "SIGNED" : "PENDING",
    signedAt: pmcSigner?.signedAtIso || pmcSignature.signedAtIso,
    x: margin,
    boxY: firstRowY,
    boxWidth: threeBoxWidth,
  });
  drawSignatureBox({
    label: "SECOND PARTY - PACS CHAIRPERSON",
    name:
      pacsSigner?.chairperson?.name ||
      pacsSignature.chairperson?.name ||
      data.chairpersonName,
    designation: "Chairperson",
    signatureDataUrl: resolvedPacsChairDataUrl,
    status:
      resolvedPacsChairDataUrl
        ? "SIGNED"
        : "PENDING",
    signedAt: pacsSigner?.signedAtIso || pacsSignature.signedAtIso,
    x: margin + threeBoxWidth + signatureGap,
    boxY: firstRowY,
    boxWidth: threeBoxWidth,
  });
  drawSignatureBox({
    label: "SECOND PARTY - PACS SECRETARY",
    name:
      pacsSigner?.secretary?.name ||
      pacsSignature.secretary?.name ||
      data.secretaryName,
    designation: "Secretary",
    signatureDataUrl: resolvedPacsSecretaryDataUrl,
    status:
      resolvedPacsSecretaryDataUrl
        ? "SIGNED"
        : "PENDING",
    signedAt: pacsSigner?.signedAtIso || pacsSignature.signedAtIso,
    x: margin + (threeBoxWidth + signatureGap) * 2,
    boxY: firstRowY,
    boxWidth: threeBoxWidth,
  });
  y += 160;

  paragraph(
    "IN WITNESS WHEREOF, the duly authorised representatives of the First Party and Second Party have executed this MoU on the date and place first written above and have initialled/signed every page as an integral part of this instrument.",
    { bold: true, size: 7.5, after: 9 }
  );

  paragraph("Party identities, authorized signatures and signing dates are drawn from the signing records. Where legally required, witness attestation, stamping or prescribed execution shall be completed through the applicable execution process and retained with the Project records; this generated document does not substitute for those formalities.", { size: 9, after: 9 });

  const totalPages = pdf.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    addLegalPageFrame();
    addHeader(pdf, data, page, totalPages);
    addPageExecutionBand();
    addFooter(pdf, data, page, totalPages);
  }

  pdf.setProperties({
    title: `Memorandum of Understanding - ${data.applicationId}`,
    subject: "PMC and PACS two-party Project MOU",
    author: `${data.pmcName} and ${data.pacsName}`,
    creator: "NAFED Grain Storage Digital Portal",
    keywords: "PMC, PACS, DPR, Grain Storage, Two-Party Memorandum of Understanding",
  });

  return {
    pdf,
    blob: pdf.output("blob"),
    fileName: `PMC_PACS_MOU_${safeName(
      data.applicationId
    )}_V11.pdf`,
    mouNumber: data.mouNumber,
    templateVersion: data.templateVersion,
    data,
    missingFields: getMissingMouFields(data),
  };
}

// Firestore map key order is not a document change.
export function sameMouRecord(left, right) {
  const normalize = (value) => {
    if (Array.isArray(value)) return value.map(normalize);
    if (value && typeof value === "object") {
      return Object.fromEntries(Object.keys(value).sort().map(key => [key, normalize(value[key])]));
    }
    return value;
  };
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

export function getMouDataSnapshot(profile = {}, project = {}) {
  const d = buildMouData(profile, project);
  const keys = ["applicationId","pacsName","pacsRegistrationNumber","pacsGoverningLaw","pacsAddress","chairpersonName","secretaryName","boardResolutionNumber","boardResolutionDate","location","village","taluka","district","state","capacityMt","landSurveyNumber","landArea","latitude","longitude","projectDurationDays","dlpPeriod","lender","lenderBranch","pmcName","pmcRegistrationNumber","pmcAddress","pmcLoaNumber","pmcLoaDate","executionPlace","stampValue","projectCost","pmcSignatoryName","pmcSignatoryDesignation"];
  const snapshot = Object.fromEntries(keys.map(key => [key, String(d[key] ?? "")]));
  for (const key of ["dprApprovalReference","dprApprovalDate","baselineDate","loanSanctionReference","pmcAuthorityReference","pmcAuthorityDate","mouStampCertificateNumber","mouEffectiveDate"]) snapshot[key] = String(project[key] ?? "");
  return JSON.stringify(snapshot);
}
export function isCurrentMouSignature(project = {}, signature = {}) {
  if (signature.templateVersion === MOU_TEMPLATE_VERSION && signature.dataSnapshot) {
    return project.mouTemplateVersion === MOU_TEMPLATE_VERSION &&
      upperText(signature.status) === "SIGNED" &&
      signature.dataSnapshot === project.mouDraftDataSnapshot;
  }
  const signed = new Date(signature.signedAtIso || "").getTime();
  const generated = new Date(project.mouGeneratedAtIso || "").getTime();
  return project.mouTemplateVersion === MOU_TEMPLATE_VERSION &&
    upperText(signature.status) === "SIGNED" &&
    Number.isFinite(signed) && Number.isFinite(generated) && signed >= generated &&
    (!signature.templateVersion || signature.templateVersion === MOU_TEMPLATE_VERSION);
}

export function getMouSigningState(project = {}, profile = {}) {
  const unchanged = project.mouDraftDataSnapshot === getMouDataSnapshot(profile,project);
  const pmc = unchanged && isCurrentMouSignature(project,project.mouSignatures?.pmc || {});
  const pacs = unchanged && isCurrentMouSignature(project,project.mouSignatures?.pacs || {});
  return {unchanged,pmc,pacs,status:pmc ? (pacs ? "FULLY_EXECUTED" : "PENDING_PACS_SIGNATURE") : "PENDING_PMC_SIGNATURE"};
}

export function getCurrentFinalMouUrl(project = {}, profile = {}) {
  const signatures = project.mouSignatures || {};
  if (project.mouDraftDataSnapshot !== getMouDataSnapshot(profile,project) ||
      !isCurrentMouSignature(project,signatures.pmc) || !isCurrentMouSignature(project,signatures.pacs) ||
      project.mouStage !== "FULLY_EXECUTED") return "";
  // The PACS execution record binds this URL to the current signature; legacy project URLs alone do not.
  return signatures.pacs.finalSignedMouUrl ||
    (project.mouFinalTemplateVersion === MOU_TEMPLATE_VERSION ? (project.finalSignedMouUrl || project.finalMouUrl || "") : "");
}

async function loadExistingSignatureImages(project = {}) {
  const signatures = project.mouSignatures || {};
  const pmcValid = isCurrentMouSignature(project, signatures.pmc);
  const pacsValid = isCurrentMouSignature(project, signatures.pacs);
  return {
    pmcSignatureDataUrl: pmcValid ? await urlToDataUrl(firstValue(signatures.pmc?.signatureDataUrl, signatures.pmc?.signatureUrl, project.pmcSignatureUrl)) : "",
    pacsChairSignatureDataUrl: pacsValid ? await urlToDataUrl(signatures.pacs?.chairperson?.signatureDataUrl || getPacsSignatureUrl(signatures.pacs, "chairperson")) : "",
    pacsSecretarySignatureDataUrl: pacsValid ? await urlToDataUrl(signatures.pacs?.secretary?.signatureDataUrl || getPacsSignatureUrl(signatures.pacs, "secretary")) : "",
  };
}

async function safeSetDocument(reference, payload, options) {
  try {
    await setDoc(reference, payload, options);
    return true;
  } catch (error) {
    console.warn(
      "Optional notification/audit write was not permitted; the main MOU transaction remains valid.",
      error
    );
    return false;
  }
}

export async function generateMouDraftAndUpload({
  profile = {},
  project = {},
  actorUid = "",
  actorEmail = "",
}) {
  if (!project.id) throw new Error("Project ID is required.");
  const output = createMouPdf({ profile, project });
  if (output.missingFields.length) {
    throw new Error(
      `Complete the required MOU fields: ${output.missingFields.join(", ")}`
    );
  }

  const generatedAtIso = new Date().toISOString();
  const hash = await sha256(output.blob);
  const path = `nafed_projects/${project.id}/mou/draft/${safeName(
    output.fileName
  )}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, output.blob, {
    contentType: "application/pdf",
    customMetadata: {
      projectId: project.id,
      applicationId: output.data.applicationId,
      mouNumber: output.mouNumber,
      templateVersion: output.templateVersion,
      sha256: hash || "NOT_AVAILABLE",
      generatedByUid: actorUid,
    },
  });
  const url = await getDownloadURL(storageRef);

  await updateDoc(doc(db, "godown_projects", project.id), {
    mouNumber: output.mouNumber,
    mouVersion: Number(project.mouVersion || 1),
    mouTemplateVersion: output.templateVersion,
    mouDraftUrl: url,
    mouDraftStoragePath: path,
    mouDraftSha256: hash,
    mouGeneratedAtIso: generatedAtIso,
    mouDocumentProfile: project.mouDocumentProfile || profile,
    mouDraftDataSnapshot: getMouDataSnapshot(profile, project),
    mouGeneratedAt: serverTimestamp(),
    mouGeneratedByUid: actorUid,
    mouGeneratedByEmail: actorEmail,
    mouAgreementType: "PMC_PACS_TWO_PARTY",
    mouSigningOrder: SIGNING_ORDER,
    mouStage: "PENDING_PMC_SIGNATURE",
    mouStatus: "PENDING_PMC_SIGNATURE",
    currentStage: "MOU Pending PMC Signature",
    updatedAt: serverTimestamp(),
  });

  return {
    mouNumber: output.mouNumber,
    mouDraftUrl: url,
    mouDraftStoragePath: path,
    mouDraftSha256: hash,
    mouTemplateVersion: output.templateVersion,
    mouStage: "PENDING_PMC_SIGNATURE",
    mouGeneratedAtIso: generatedAtIso,
    mouDocumentProfile: project.mouDocumentProfile || profile,
    mouDraftDataSnapshot: getMouDataSnapshot(profile, project),
  };
}


// A saved image can be reused only by the recorded/assigned PMC Auth user.
// Firestore and Storage rules remain authoritative and must enforce this ownership.
export function canReacceptPmcRevision(project = {}, user = null) {
  if (!user?.uid) return false;
  const pacsUid = clean(project.pacsOwnerAuthUid, "");
  if (pacsUid && pacsUid === user.uid) return false;
  const allowed = [project.mouSignatures?.pmc?.signedByUid, project.pmcUid, project.assignedPmcUid, project.pmcAuthUid]
    .map(value => clean(value, "")).filter(Boolean);
  return allowed.includes(user.uid);
}

export async function reaffirmPmcMouRevision({profile = {}, project = {}, accepted = false}) {
  const user = auth.currentUser;
  if (!accepted) throw new Error("Read the current MoU and explicitly accept this revision first.");
  if (!project.id || !canReacceptPmcRevision(project, user)) {
    throw new Error("Use the recorded PMC signer's authorized login to accept this revision. PACS cannot accept for PMC.");
  }
  const reference = doc(db, "godown_projects", project.id);
  const liveDoc = await getDoc(reference);
  if (!liveDoc.exists()) throw new Error("Project record no longer exists.");
  const live = {...liveDoc.data(), id: project.id};
  if (!canReacceptPmcRevision(live, user)) throw new Error("PMC authorization changed. Refresh the Project.");
  const snapshot = getMouDataSnapshot(profile, live);
  if (snapshot !== getMouDataSnapshot(profile, project)) throw new Error("Project details changed. Refresh and preview the current MoU before accepting.");
  const previous = live.mouSignatures?.pmc || {};
  const signatureUrl = firstValue(previous.signatureUrl, live.pmcSignatureUrl);
  if (!signatureUrl) throw new Error("No saved PMC signature image is available. Complete PMC signing from the PMC signing screen.");
  if (!previous.name || !previous.designation) throw new Error("The saved PMC signatory name/designation is incomplete. Update it through the PMC signing screen.");
  const image = await urlToDataUrl(previous.signatureDataUrl || signatureUrl);
  if (!image) throw new Error("Saved PMC signature could not be loaded. Check its access or upload it from the PMC signing screen.");
  const generatedAtIso = new Date().toISOString();
  const signedAtIso = generatedAtIso;
  const signature = {status:"SIGNED", party:"PMC", signingOrderPosition:1, signatureStoragePath:previous.signatureStoragePath || "", name:previous.name, designation:previous.designation,
    signatureUrl, signatureDataUrl:image, signedAtIso, signedByUid:user.uid, signedByEmail:user.email || "",
    templateVersion:MOU_TEMPLATE_VERSION, dataSnapshot:snapshot, consentAccepted:true,
    consentTextVersion:MOU_TEMPLATE_VERSION, consentText:"I have reviewed and accept this MoU revision and authorize reuse of my saved PMC signature image.",
    previousSignedAtIso:previous.signedAtIso || ""};
  const revised = {...live, mouTemplateVersion:MOU_TEMPLATE_VERSION, mouGeneratedAtIso:generatedAtIso,
    mouDraftDataSnapshot:snapshot, mouFullyExecutedAtIso:"", mouSignatures:{...live.mouSignatures, pmc:signature}};
  const output = createMouPdf({profile, project:revised, pmcSignatureDataUrl:image, pmcSigner:signature});
  if (output.missingFields.length) throw new Error(`Complete Project records: ${output.missingFields.join(", ")}`);
  const pdfHash = await sha256(output.blob);
  const path = `nafed_projects/${live.id}/mou/draft/${Date.now()}_${output.fileName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, output.blob, {contentType:"application/pdf", customMetadata:{projectId:live.id,
    templateVersion:MOU_TEMPLATE_VERSION, signedByUid:user.uid, stage:"PENDING_PACS_SIGNATURE"}});
  const url = await getDownloadURL(storageRef);
  signature.signedPdfUrl = url;
  signature.signedPdfSha256 = pdfHash;
  const patch = {mouTemplateVersion:MOU_TEMPLATE_VERSION, mouGeneratedAtIso:generatedAtIso,
    mouDraftDataSnapshot:snapshot, mouDraftUrl:url, mouDraftStoragePath:path, mouDraftSha256:pdfHash,
    mouNumber:output.mouNumber, mouStage:"PENDING_PACS_SIGNATURE", mouStatus:"PENDING_PACS_SIGNATURE",
    currentStage:"MoU Pending PACS Signature", twoPartyMouExecuted:false, mouFullyExecutedAtIso:"",
    mouRevisionPreviousExecution:{templateVersion:live.mouTemplateVersion || "", pmc:previous,
      pacs:live.mouSignatures?.pacs || {}, finalUrl:getCurrentFinalMouUrl(live) || live.finalMouUrl || live.finalSignedMouUrl || live.mouFinalPdfUrl || ""}};
  const nextPacsSignature = {...(live.mouSignatures?.pacs || {}),status:"PENDING_REACCEPTANCE"};
  await runTransaction(db, async transaction => {
    const currentDoc = await transaction.get(reference);
    if (!currentDoc.exists()) throw new Error("Project was removed.");
    const current = {...currentDoc.data(), id:live.id};
    if (!canReacceptPmcRevision(current, user) || getMouDataSnapshot(profile,current) !== snapshot ||
        current.mouGeneratedAtIso !== live.mouGeneratedAtIso ||
        !sameMouRecord(current.mouSignatures || {}, live.mouSignatures || {})) {
      throw new Error("Draft or signatures changed. Refresh, review and accept again.");
    }
    transaction.update(reference, {...patch, "mouSignatures.pmc":signature,"mouSignatures.pacs":nextPacsSignature,
      mouGeneratedAt:serverTimestamp(), updatedAt:serverTimestamp()});
  });
  return {...patch, mouSignatures:{...live.mouSignatures, pmc:signature,pacs:nextPacsSignature}};
}

export default function PacsMouSigning({
  profile = {},
  profileSource = {},
  project: suppliedProject = {},
  onSaved,
}) {
  const [localProject, setLocalProject] = useState(null);
  useEffect(() => { setLocalProject(null); }, [suppliedProject]);
  const project = localProject?.id === suppliedProject.id ? {...suppliedProject, ...localProject} : suppliedProject;
  const emitSaved = patch => { setLocalProject(previous => ({...(previous?.id === suppliedProject.id ? previous : {}), ...patch, id:suppliedProject.id})); onSaved?.(patch); };
  const [editingPacsSignatures, setEditingPacsSignatures] = useState(false);
  const [pmcRevisionAccepted, setPmcRevisionAccepted] = useState(false);
  const signatures = project.mouSignatures || {};
  const data = useMemo(() => buildMouData(profile, project), [profile, project]);
  const missingFields = useMemo(() => getMissingMouFields(data), [data]);
  const [chairpersonName, setChairpersonName] = useState(
    data.chairpersonName || ""
  );
  const [secretaryName, setSecretaryName] = useState(
    data.secretaryName || ""
  );
  const [chairSignatureFile, setChairSignatureFile] = useState(null);
  const [secretarySignatureFile, setSecretarySignatureFile] = useState(null);
  const [termsRead, setTermsRead] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const pmcCanReaccept = canReacceptPmcRevision(project, auth.currentUser);
  const previousPmcSigned = upperText(signatures.pmc?.status) === "SIGNED";
  const unchangedDraft = project.mouDraftDataSnapshot === getMouDataSnapshot(profile, project);
  const pmcSigned = unchangedDraft && isCurrentMouSignature(project, signatures.pmc);
  const pacsSigned = unchangedDraft && isCurrentMouSignature(project, signatures.pacs);
  const documentApprovalRecorded = [
    project.documentStage,
    project.pmcDecision,
    project.pmcVerificationStatus,
    project.pmcCompleteDocumentSetStatus,
    project.pmcDocumentApproval?.status,
    project.documentApprovalStatus,
  ].some((value) =>
    [
      "PMC_DOCUMENTS_APPROVED",
      "APPROVED",
      "APPROVED_TO_WORKFLOW",
      "COMPLETE",
      "COMPLETED",
    ].includes(upperText(value))
  );

  // The stored PMC signature proves that the PMC approval gate was completed.
  // This prevents legacy/stale project status fields from blocking PACS signing.
  const documentApproved = documentApprovalRecorded || pmcSigned;
  const priorSignaturesComplete = pmcSigned;
  const fullySigned = pmcSigned && pacsSigned;
  const pacsBlockingFields = useMemo(() => {
    const liveSignerNames = {
      "Chairperson name": clean(chairpersonName, ""),
      "Secretary name": clean(secretaryName, ""),
    };

    // PMC validates the complete project data before signing. After that point,
    // PACS is only responsible for its two live signatory particulars.
    if (pmcSigned) {
      return Object.entries(liveSignerNames)
        .filter(([, value]) => !value)
        .map(([label]) => label);
    }

    return missingFields.filter((field) => {
      if (Object.prototype.hasOwnProperty.call(liveSignerNames, field)) {
        return !liveSignerNames[field];
      }
      return true;
    });
  }, [chairpersonName, missingFields, pmcSigned, secretaryName]);
  const pacsCanSign =
    !pmcCanReaccept &&
    documentApproved &&
    priorSignaturesComplete &&
    (!pacsSigned || editingPacsSignatures) &&
    pacsBlockingFields.length === 0;
  const finalUrl = getCurrentFinalMouUrl(project,profile);
  const mouNumber = data.mouNumber;
  const storedChairSignatureUrl = firstValue(
    getPacsSignatureUrl(signatures.pacs, "chairperson"),
    project.chairpersonSignatureUrl,
    profile.chairpersonSignatureUrl,
    profile.chairmanSignatureUrl
  );
  const storedSecretarySignatureUrl = firstValue(
    getPacsSignatureUrl(signatures.pacs, "secretary"),
    project.secretarySignatureUrl,
    profile.secretarySignatureUrl
  );

  const showMessage = (text, type = "info") =>
    setMessage({ text, type });

  const refreshSigningStatus = async () => {
    try {
      setBusy("refresh");
      const record = await getDoc(doc(db, "godown_projects", project.id));
      if (!record.exists()) throw new Error("Project record not found.");
      emitSaved({...record.data(), id:project.id});
      setPmcRevisionAccepted(false); setTermsRead(false); setAccepted(false);
      showMessage("Signature status refreshed from the Project record.", "success");
    } catch (error) { showMessage(error.message || "Unable to refresh signing status.", "error"); }
    finally { setBusy(""); }
  };
  const acceptPmcRevision = async () => {
    try {
      if (!termsRead || !pmcRevisionAccepted) throw new Error("Preview/read the current MoU and confirm PMC acceptance first.");
      setBusy("pmc-reaccept");
      const patch = await reaffirmPmcMouRevision({profile, project, accepted:true});
      emitSaved(patch); setPmcRevisionAccepted(false); setTermsRead(false);
      showMessage("PMC accepted the current revision using its saved signature. PACS can now refresh and sign.", "success");
    } catch (error) { showMessage(error.message || "PMC acceptance failed.", "error"); }
    finally { setBusy(""); }
  };

  const assertSignatureFile = (file, label, existingUrl) => {
    if (!file && !existingUrl) {
      throw new Error(`${label} signature is required.`);
    }
    if (!file) return;
    if (!isSupportedSignatureImage(file)) {
      throw new Error(`${label} signature must be PNG or JPG.`);
    }
    if (file.size > MAX_SIGNATURE_SIZE) {
      throw new Error(`${label} signature cannot exceed 2 MB.`);
    }
  };

  const previewDraft = async () => {
    try {
      const existingSignatures = unchangedDraft ? await loadExistingSignatureImages(project) : {};
      const output = createMouPdf({
        profile,
        project,
        ...existingSignatures,
      });
      const url = output.pdf.output("bloburl");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(error);
      showMessage(error.message || "MOU preview failed.", "error");
    }
  };

  const downloadDraft = async () => {
    try {
      const existingSignatures = unchangedDraft ? await loadExistingSignatureImages(project) : {};
      const output = createMouPdf({
        profile,
        project,
        ...existingSignatures,
      });
      output.pdf.save(output.fileName);
    } catch (error) {
      console.error(error);
      showMessage(error.message || "MOU download failed.", "error");
    }
  };

  const uploadPacsSignature = async ({
    file,
    existingUrl,
    existingDataUrl,
    role,
    user,
  }) => {
    if (!file) {
      return {
        signatureUrl: existingUrl,
        signatureStoragePath: "",
        dataUrl: await urlToDataUrl(existingDataUrl || existingUrl),
        source: "SAVED_PROFILE_SIGNATURE",
      };
    }

    const dataUrl = await compactSignatureDataUrl(await fileToDataUrl(file));
    const path = `nafed_pacs/${user.uid}/${project.id}/mou/signatures/${role}_${Date.now()}_${safeName(
      file.name
    )}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, {
      contentType:
        file.type || (/\.png$/i.test(file.name) ? "image/png" : "image/jpeg"),
      customMetadata: {
        projectId: project.id,
        applicationId: data.applicationId,
        party: "PACS",
        role,
        signedByUid: user.uid,
      },
    });
    return {
      signatureUrl: await getDownloadURL(storageRef),
      signatureStoragePath: path,
      dataUrl,
      source: "PROJECT_UPLOAD",
    };
  };

  const signAndFinalize = async () => {
    const user = auth.currentUser;
    let operation = "";
    try {
      if (!user || !project.id) {
        throw new Error("Application or login session is not available.");
      }
      if (!documentApproved) {
        throw new Error(
          "PMC must approve the complete PACS document set before MOU execution."
        );
      }
      if (!pmcSigned) {
        throw new Error("PMC must sign the MOU first.");
      }
      if (pacsSigned && !editingPacsSignatures) throw new Error("Choose Update PACS Signatures to replace the signed images.");
      if (pmcCanReaccept) throw new Error("Use the authorized PACS login for PACS execution.");
      if (pacsBlockingFields.length) {
        throw new Error(
          `Complete the required MOU fields: ${pacsBlockingFields.join(", ")}`
        );
      }
      if (!clean(chairpersonName, "")) {
        throw new Error("Enter the authorized Chairperson name.");
      }
      if (!clean(secretaryName, "")) {
        throw new Error("Enter the authorized Secretary name.");
      }
      assertSignatureFile(
        chairSignatureFile,
        "Chairperson",
        storedChairSignatureUrl
      );
      assertSignatureFile(
        secretarySignatureFile,
        "Secretary",
        storedSecretarySignatureUrl
      );
      if (clean(chairpersonName, "") !== clean(data.chairpersonName, "") || clean(secretaryName, "") !== clean(data.secretaryName, "")) {
        throw new Error("Signatory names changed. Update the project details and obtain fresh PMC acceptance of the revised draft first.");
      }
      if (!termsRead || !accepted) {
        throw new Error(
          "Read the MOU and accept the Board authorization declaration."
        );
      }
      if (
        !window.confirm(
          "Apply the authorized Chairperson and Secretary signatures and create the final locked MoU?"
        )
      ) {
        return;
      }

      const liveProjectDoc = await getDoc(doc(db, "godown_projects", project.id));
      if (!liveProjectDoc.exists()) throw new Error("Project record no longer exists.");
      const liveProject = { ...liveProjectDoc.data(), id: project.id };
      if (liveProject.mouDraftDataSnapshot !== getMouDataSnapshot(profile, liveProject) ||
          liveProject.mouDraftDataSnapshot !== project.mouDraftDataSnapshot ||
          !isCurrentMouSignature(liveProject, liveProject.mouSignatures?.pmc) ||
          liveProject.mouSignatures?.pmc?.signedAtIso !== signatures.pmc?.signedAtIso ||
          liveProject.mouSignatures?.pmc?.signatureUrl !== signatures.pmc?.signatureUrl) {
        throw new Error("The draft or PMC signature changed. Refresh and review the current MOU before signing.");
      }
      if (!editingPacsSignatures && isCurrentMouSignature(liveProject, liveProject.mouSignatures?.pacs)) throw new Error("This revision is already signed. Refresh to view the final MOU.");
      setBusy("sign");
      setMessage({ type: "", text: "" });
      const signedAtIso = new Date().toISOString();

      const ownerProfileId = clean(
        profileSource.documentId || profile.id || user.uid,
        "",
      );
      const ownerProfileCollection = clean(
        profileSource.collectionName,
        "",
      );
      if (!ownerProfileCollection || !ownerProfileId) {
        throw new Error(
          "PACS login profile could not be linked. Please logout and login once.",
        );
      }
      if (
        clean(project.pacsOwnerAuthUid, "") &&
        clean(project.pacsOwnerAuthUid, "") !== user.uid
      ) {
        throw new Error("This MOU is linked with another PACS login.");
      }

      // Bind an older project to the verified PACS login before uploading.
      // Subsequent project writes are authorized by this immutable Auth UID.
      if (clean(project.pacsOwnerAuthUid, "") !== user.uid) {
        operation = "PACS project owner link";
        await updateDoc(doc(db, "godown_projects", project.id), {
          pacsOwnerAuthUid: user.uid,
          pacsOwnerProfileId: ownerProfileId,
          pacsOwnerProfileCollection: ownerProfileCollection,
          pacsOwnerBoundAt: serverTimestamp(),
          pacsOwnerBoundAtIso: signedAtIso,
        });
      }

      operation = "PACS signature upload";
      const [chairSignature, secretarySignature, existingImages] =
        await Promise.all([
          uploadPacsSignature({
            file: chairSignatureFile,
            existingUrl: storedChairSignatureUrl,
            existingDataUrl: signatures.pacs?.chairperson?.signatureDataUrl,
            role: "chairperson",
            user,
          }),
          uploadPacsSignature({
            file: secretarySignatureFile,
            existingUrl: storedSecretarySignatureUrl,
            existingDataUrl: signatures.pacs?.secretary?.signatureDataUrl,
            role: "secretary",
            user,
          }),
          loadExistingSignatureImages({...project, mouSignatures:{pmc:signatures.pmc}}),
        ]);

      if (!existingImages.pmcSignatureDataUrl || !chairSignature.dataUrl || !secretarySignature.dataUrl) {
        throw new Error("All three signature images must load before finalization.");
      }
      const pacsSigner = {
        signedAtIso,
        chairperson: {
          name: clean(chairpersonName),
          designation: "Chairperson",
        },
        secretary: {
          name: clean(secretaryName),
          designation: "Secretary",
        },
      };

      const output = createMouPdf({
        profile,
        project,
        pmcSignatureDataUrl: existingImages.pmcSignatureDataUrl,
        pmcSigner: signatures.pmc,
        pacsChairSignatureDataUrl: chairSignature.dataUrl,
        pacsSecretarySignatureDataUrl: secretarySignature.dataUrl,
        pacsSigner,
      });

      const pdfHash = await sha256(output.blob);
      // Keep the final PDF inside the authenticated PACS namespace. Existing
      // PACS Storage rules already scope this tree to request.auth.uid; the
      // earlier shared nafed_projects path caused a Storage permission error
      // before the Firestore status update could run.
      const pdfPath = `nafed_pacs/${user.uid}/${project.id}/mou/final/${Date.now()}_${output.fileName}`;
      const pdfRef = ref(storage, pdfPath);
      operation = "Final MOU PDF upload";
      await uploadBytes(pdfRef, output.blob, {
        contentType: "application/pdf",
        customMetadata: {
          projectId: project.id,
          applicationId: data.applicationId,
          mouNumber: output.mouNumber,
          templateVersion: output.templateVersion,
          stage: "FULLY_EXECUTED",
          signingOrder: SIGNING_ORDER.join("_"),
          sha256: pdfHash || "NOT_AVAILABLE",
          finalizedByUid: user.uid,
        },
      });
      const finalSignedMouUrl = await getDownloadURL(pdfRef);

      const pacsSignature = {
        templateVersion: MOU_TEMPLATE_VERSION,
        dataSnapshot: getMouDataSnapshot(profile, project),
        status: "SIGNED",
        party: "PACS",
        signingOrderPosition: 2,
        chairperson: {
          name: pacsSigner.chairperson.name,
          designation: "Chairperson",
          signatureUrl: chairSignature.signatureUrl,
          signatureDataUrl: chairSignature.dataUrl,
          signatureStoragePath: chairSignature.signatureStoragePath,
          signatureSource: chairSignature.source,
        },
        secretary: {
          name: pacsSigner.secretary.name,
          designation: "Secretary",
          signatureUrl: secretarySignature.signatureUrl,
          signatureDataUrl: secretarySignature.dataUrl,
          signatureStoragePath: secretarySignature.signatureStoragePath,
          signatureSource: secretarySignature.source,
        },
        boardResolutionNumber: data.boardResolutionNumber,
        boardResolutionDate: data.boardResolutionDate,
        finalSignedMouUrl,
        finalSignedMouStoragePath: pdfPath,
        finalSignedMouSha256: pdfHash,
        consentAccepted: true,
        termsRead: true,
        consentTextVersion: MOU_TEMPLATE_VERSION,
        signedAtIso,
        signedByUid: user.uid,
        signedByEmail: user.email || "",
      };

      const previousPacsExecution = {signature:liveProject.mouSignatures?.pacs || {},finalUrl:getCurrentFinalMouUrl(liveProject,profile) || liveProject.finalExecutedMouUrl || liveProject.finalMouUrl || "",archivedAtIso:signedAtIso};
      operation = "PACS MOU project update";
      const finalPatch = {
        mouPreviousPacsExecution:previousPacsExecution,
        pacsOwnerAuthUid: user.uid,
        pacsOwnerProfileId: ownerProfileId,
        pacsOwnerProfileCollection: ownerProfileCollection,
        mouNumber: output.mouNumber,
        mouVersion: Number(project.mouVersion || 1),
        mouTemplateVersion: output.templateVersion,
        mouAgreementType: "PMC_PACS_TWO_PARTY",
        mouSigningOrder: SIGNING_ORDER,
        "mouSignatures.pacs": pacsSignature,
        mouFinalTemplateVersion: MOU_TEMPLATE_VERSION,
        finalExecutedMouUrl: finalSignedMouUrl,
        mouFullyExecuted: true,
        finalMouUrl: finalSignedMouUrl,
        finalSignedMouUrl,
        mouFinalPdfUrl: finalSignedMouUrl,
        mouFinalPdfStoragePath: pdfPath,
        mouFinalPdfSha256: pdfHash,
        mouFullyExecutedAtIso: signedAtIso,
        mouFullyExecutedAt: serverTimestamp(),
        twoPartyMouExecuted: true,
        mouStage: "FULLY_EXECUTED",
        mouStatus: "FULLY_EXECUTED",
        currentStage: "MoU Fully Executed",
        progress: Math.max(Number(project.progress || 0), 60),
        updatedAt: serverTimestamp(),
      };

      await runTransaction(db, async transaction => {
        const reference = doc(db,"godown_projects",project.id);
        const record = await transaction.get(reference);
        if (!record.exists()) throw new Error("Project no longer exists.");
        const fresh = {...record.data(),id:project.id};
        if (fresh.mouDraftDataSnapshot !== getMouDataSnapshot(profile,fresh) ||
            fresh.mouDraftDataSnapshot !== liveProject.mouDraftDataSnapshot ||
            !sameMouRecord(fresh.mouSignatures || {}, liveProject.mouSignatures || {})) {
          throw new Error("The signing record changed. Refresh and review before retrying.");
        }
        transaction.update(reference,finalPatch);
      });

      await safeSetDocument(
        doc(db, "nafed_notifications", `${project.id}_MOU_FULLY_EXECUTED`),
        {
          type: "MOU_FULLY_EXECUTED",
          title: "MoU Fully Executed",
          message: `${data.pacsName}: PMC and PACS signatures are complete. State Branch may view or download the executed copy.`,
          projectId: project.id,
          applicationId: data.applicationId,
          mouNumber: output.mouNumber,
          pacsUid: user.uid,
          pmcUid: project.pmcId || project.pmcUid || "",
          visibleTo: ["PMC", "PACS", "STATE_BRANCH", "HEAD_OFFICE"],
          status: "ACTIVE",
          readBy: [],
          createdAt: serverTimestamp(),
          createdAtIso: signedAtIso,
        },
        { merge: true }
      );

      await safeSetDocument(
        doc(
          db,
          "nafed_activity",
          `${project.id}_PACS_MOU_FINAL_EXECUTED_${Date.now()}`
        ),
        {
          action: "PACS_MOU_FINAL_EXECUTED",
          entityType: "PMC_PACS_MOU",
          entityId: project.id,
          projectId: project.id,
          applicationId: data.applicationId,
          mouNumber: output.mouNumber,
          previousStage: project.mouStage || "PENDING_PACS_SIGNATURE",
          newStage: "FULLY_EXECUTED",
          performedByUid: user.uid,
          performedByEmail: user.email || "",
          performedByRole: "PACS",
          pdfSha256: pdfHash,
          createdAt: serverTimestamp(),
          createdAtIso: signedAtIso,
        }
      );

      emitSaved({
        mouNumber: output.mouNumber,
        mouTemplateVersion: output.templateVersion,
        mouAgreementType: "PMC_PACS_TWO_PARTY",
        mouSigningOrder: SIGNING_ORDER,
        mouSignatures: { ...signatures, pacs: pacsSignature },
        mouFinalTemplateVersion: MOU_TEMPLATE_VERSION,
        finalExecutedMouUrl: finalSignedMouUrl,
        mouFullyExecuted: true,
        finalMouUrl: finalSignedMouUrl,
        finalSignedMouUrl,
        mouFinalPdfUrl: finalSignedMouUrl,
        mouStage: "FULLY_EXECUTED",
        mouStatus: "FULLY_EXECUTED",
        twoPartyMouExecuted: true,
        currentStage: "MoU Fully Executed",
      });

      setEditingPacsSignatures(false);
      setChairSignatureFile(null);
      setSecretarySignatureFile(null);
      setTermsRead(false);
      setAccepted(false);
      showMessage(
        "PACS Chairperson and Secretary signatures recorded. The final locked MoU is ready.",
        "success"
      );
    } catch (error) {
      console.error("PACS MOU final execution failed", operation, error);
      const detail = error.message || "MOU execution failed.";
      showMessage(operation ? `${operation} failed: ${detail}` : detail, "error");
    } finally {
      setBusy("");
    }
  };

  const party = (title, value, order) => {
    const pacsValue = title === "PACS / PROJECT OWNER";
    const signatureImage = pacsValue
      ? getPacsSignatureUrl(value, "chairperson")
      : value?.signatureUrl;
    const displayName = pacsValue
      ? [value?.chairperson?.name, value?.secretary?.name]
          .filter(Boolean)
          .join(" + ")
      : value?.name;
    const currentlySigned = pacsValue ? pacsSigned : pmcSigned;
    const designation = pacsValue
      ? "Chairperson + Secretary"
      : value?.designation;
    return (
      <article className="mou-party">
        <div className="party-order">{order}</div>
        <h3>{title}</h3>
        <div className="signature-preview">
          {signatureImage ? (
            <img src={signatureImage} alt={`${title} signature`} />
          ) : (
            <span>Signature Pending</span>
          )}
        </div>
        <b>{displayName || "Authorised Signatory"}</b>
        <small>{designation || "Designation pending"}</small>
        <Status value={currentlySigned ? "SIGNED" : "PENDING"} />
        
        {value?.signedAtIso && <em>{formatDate(value.signedAtIso)}</em>}
      </article>
    );
  };

  return (
    <div className="mou-page">
      <style>{STYLE}</style>

      <div className="gov-ribbon" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <header className="mou-header">
        <div className="gov-identity">
          <div className="gov-mark" aria-label="NAFED">
            <strong>NAFED</strong>
            <span>Government Programme</span>
          </div>
          <div>
            <small>OFFICIAL MOU EXECUTION WORKSPACE</small>
            <h1>Memorandum of Understanding (MoU)</h1>
            <p>Appointed PMC | PACS / Project Owner</p>
          </div>
        </div>
        <div className="mou-stage">
          <span>MOU Number</span>
          <b>{mouNumber}</b>
          <em>{project.mouStage || "PENDING_PMC_SIGNATURE"}</em>
        </div>
      </header>

      {message.text && (
        <div className={`mou-message ${message.type}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage({ type: "", text: "" })}>x</button>
        </div>
      )}

      {!documentApproved && (
        <div className="warning">
          <b>MOU execution locked</b>
          <span>The complete PACS document set must first be approved by PMC.</span>
        </div>
      )}

      <section className="project-summary">
        <Info label="PACS / Society" value={data.pacsName} />
        <Info label="Application ID" value={data.applicationId} />
        <Info
          label="Capacity"
          value={`${formatInteger(data.capacityMt)} MT`}
        />
        <Info label="Project Location" value={data.location} />
        <Info label="DPR Project Cost" value={data.projectCostText} />
        <Info
          label="Preliminary Expense Basis"
          value={data.preliminaryExpenseBasisText}
        />
      </section>

      <section className="mou-card readiness-card">
        <div className="card-heading">
          <div>
            <small>PROJECT-DATA READINESS</small>
            <h2>
              {pacsBlockingFields.length
                ? `${pacsBlockingFields.length} required field(s) pending`
                : "MoU data is complete"}
            </h2>
            <p>
              The PDF fills itself from the PACS, Project and allocated PMC records.
            </p>
          </div>
          <b
            className={`readiness-status ${
              pacsBlockingFields.length ? "pending" : "ready"
            }`}
          >
            {pacsBlockingFields.length ? "DATA REQUIRED" : "READY TO EXECUTE"}
          </b>
        </div>
        {pacsBlockingFields.length > 0 && (
          <div className="missing-fields">
            {pacsBlockingFields.map((field) => (
              <span key={field}>{field}</span>
            ))}
          </div>
        )}
      </section>

      <section className="mou-card">
        <div className="card-heading">
          <div>
            <small>COMPLETE DOCUMENT</small>
            <h2>Legal Stamp-Paper-Ready Memorandum of Understanding</h2>
            <p>
              Minimal Rs. 300 stamp-paper-ready cover, 25 legal Articles, five
              Schedules, witnesses and PMC/PACS signature or initial space on every page.
            </p>
          </div>
          <div>
            <button onClick={previewDraft}>Preview Full MoU</button>
            <button onClick={downloadDraft}>Download Draft PDF</button>
          </div>
        </div>
        <label className="terms-read">
          <input
            type="checkbox"
            checked={termsRead}
            onChange={(event) => setTermsRead(event.target.checked)}
            disabled={pacsSigned && !editingPacsSignatures}
          />
          <span>
            I have opened/read the complete MoU and verified the PACS,
            project, payment, preliminary-expense and signature particulars.
          </span>
        </label>
      </section>

      <section className="mou-card">
        <div className="card-heading">
          <div>
            <small>MANDATORY SIGNING ORDER</small>
            <h2>PMC → PACS</h2>
            <p>PMC signs first; PACS signs second and creates the final locked document.</p>
          </div>
          <b className="execution-status">
            {fullySigned ? "FULLY EXECUTED" : "SIGNATURES IN PROGRESS"}
          </b>
        </div>
        <div className="mou-parties">
          {party("DESIGNATED PMC", signatures.pmc, "1")}
          {party("PACS / PROJECT OWNER", signatures.pacs, "2")}
        </div>
        <button type="button" className="btn secondary" disabled={!!busy} onClick={refreshSigningStatus}>Refresh Signature Status</button>
        {!pmcSigned && <div className="order-lock">PMC login se signature upload karke Submit karein. Uske baad PACS yahan refresh karke sign kar sakta hai.</div>}
      </section>

      <section className="mou-card">
        <div className="card-heading">
          <div>
            <small>FINAL PACS EXECUTION</small>
            <h2>Apply Chairperson and Secretary Signatures</h2>
            <p>
              Both signatures must be authorized by the certified PACS Board /
              General Body Resolution.
            </p>
          </div>
        </div>

        {pacsSigned && !editingPacsSignatures && <button type="button" onClick={() => {setEditingPacsSignatures(true);setAccepted(false);setTermsRead(false);}}>Update PACS Signatures</button>}
        {editingPacsSignatures && <div className="order-lock">Upload Chairperson/Secretary signatures, accept the declaration and submit.<button type="button" onClick={() => {setEditingPacsSignatures(false);setChairSignatureFile(null);setSecretarySignatureFile(null);setAccepted(false);setTermsRead(false);}}>Cancel Update</button></div>}
        {pacsSigned && !editingPacsSignatures ? (
          <div className="signed-note">
            <b>PACS signatures completed</b>
            <span>The final MoU has been locked.</span>
            {finalUrl && (
              <a href={finalUrl} target="_blank" rel="noreferrer">
                View Final Signed MoU
              </a>
            )}
          </div>
        ) : (
          <>
            {!priorSignaturesComplete && (
              <div className="order-lock">
                {!pmcSigned
                  ? "PMC must upload and submit its signature first. Then refresh this page."
                  : "PMC signature is ready. PACS can now complete final execution."}
              </div>
            )}
            <div className="sign-grid">
              <label>
                <span>Chairperson Name *</span>
                <input
                  value={chairpersonName}
                  onChange={(event) => setChairpersonName(event.target.value)}
                  placeholder="Full legal name"
                />
              </label>
              <label className="signature-file">
                <span>Chairperson Signature (PNG/JPG) *</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(event) =>
                    setChairSignatureFile(event.target.files?.[0] || null)
                  }
                />
                <small>
                  {chairSignatureFile?.name ||
                    (storedChairSignatureUrl
                      ? "Saved authorized signature will be used"
                      : "Maximum file size: 2 MB")}
                </small>
              </label>
              <label>
                <span>Secretary Name *</span>
                <input
                  value={secretaryName}
                  onChange={(event) => setSecretaryName(event.target.value)}
                  placeholder="Full legal name"
                />
              </label>
              <label className="signature-file">
                <span>Secretary Signature (PNG/JPG) *</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(event) =>
                    setSecretarySignatureFile(event.target.files?.[0] || null)
                  }
                />
                <small>
                  {secretarySignatureFile?.name ||
                    (storedSecretarySignatureUrl
                      ? "Saved authorized signature will be used"
                      : "Maximum file size: 2 MB")}
                </small>
              </label>
            </div>

            <label className="consent">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <span>
                I confirm that the certified PACS resolution authorizes both
                signatories and the use of their stored/uploaded signatures; the
                project details are correct; and PACS accepts the full MoU,
                preliminary-expense obligation, service-fee schedule, Escrow
                payment mandate, indemnity and Project Owner responsibilities.
              </span>
            </label>

            <button
              className="sign-button"
              onClick={signAndFinalize}
              disabled={Boolean(busy) || !pacsCanSign || !termsRead || !accepted}
            >
              {busy === "sign"
                ? "Creating Final Locked MoU..."
                : editingPacsSignatures ? "Save Updated PACS Signatures & Finalize MoU" : "Apply Both PACS Signatures & Finalize MoU"}
            </button>
          </>
        )}
      </section>

      <section className="final-mou">
        <div>
          <small>FINAL EXECUTED DOCUMENT</small>
          <h2>Locked Memorandum of Understanding</h2>
          <p>
            Available after PMC, PACS Chairperson and PACS Secretary signatures
            are recorded.
          </p>
        </div>
        {fullySigned && finalUrl ? (
          <a href={finalUrl} target="_blank" rel="noreferrer">
            Download Final Signed MoU
          </a>
        ) : (
          <button disabled>
            {fullySigned
              ? "Final PDF is being generated"
              : "Waiting for mandatory signatures"}
          </button>
        )}
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="info">
      <span>{label}</span>
      <b>{value || "Not available"}</b>
    </div>
  );
}

function Status({ value }) {
  const text = String(value || "PENDING").replaceAll("_", " ");
  return (
    <span
      className={
        /signed|executed|approved/i.test(text) ? "status signed" : "status"
      }
    >
      {text}
    </span>
  );
}

const BASE_STYLE = `
.mou-page{color:#1E2A38;font-family:Inter,"Segoe UI",Arial,sans-serif}.mou-page *{box-sizing:border-box}.mou-header{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:22px;border-radius:16px;color:#fff;background:linear-gradient(135deg,#0A2747,#123B69 58%,#2F73B8);box-shadow:0 14px 35px rgba(18,59,105,.18)}.mou-header small,.card-heading small,.final-mou small{color:#D9E8F7;font-size:9px;font-weight:900;letter-spacing:.09em}.mou-header h1{margin:6px 0 4px;font-size:26px}.mou-header p{margin:0;color:#E8F1FA;font-size:11px}.mou-stage{min-width:250px;padding:11px;border:1px solid rgba(255,255,255,.26);border-radius:10px;background:rgba(255,255,255,.12)}.mou-stage span,.mou-stage b,.mou-stage em{display:block}.mou-stage span{color:#D8E6F4;font-size:8px;text-transform:uppercase}.mou-stage b{margin:5px 0;font-size:10px;overflow-wrap:anywhere}.mou-stage em{color:#BFD8F0;font-size:8px;font-style:normal;font-weight:900}.mou-message,.warning{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:13px;padding:12px 14px;border:1px solid #B8CDE2;border-radius:9px;background:#EEF5FC;color:#164F88;font-size:11px;font-weight:800}.mou-message.success{border-color:#B6CEE6;background:#EAF2FB;color:#155B9A}.mou-message.error{border-color:#efb5b5;background:#fff0f0;color:#b3261e}.mou-message button{border:0;background:transparent;color:inherit;font-size:15px}.warning{justify-content:flex-start;border-color:#e5c46d;background:#fff7df;color:#7a5700}.warning b,.warning span{display:block}.project-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;margin:14px 0;overflow:hidden;border:1px solid #CBD8E6;border-radius:12px;background:#CBD8E6}.info{min-width:0;padding:13px;background:#fff}.info span,.info b{display:block}.info span{color:#617286;font-size:8px;text-transform:uppercase}.info b{margin-top:5px;overflow-wrap:anywhere;font-size:10px}.mou-card{padding:18px;margin-bottom:14px;border:1px solid #CBD8E6;border-radius:13px;background:#fff;box-shadow:0 7px 22px rgba(18,59,105,.05)}.card-heading{display:flex;justify-content:space-between;align-items:center;gap:15px;margin-bottom:14px}.card-heading small{color:#1D5FA7}.card-heading h2{margin:4px 0;font-size:17px}.card-heading p{margin:0;color:#617286;font-size:9px}.card-heading>div:last-child{display:flex;gap:7px}.mou-page button,.mou-page a{display:inline-block;border:1px solid #9CB8D3;border-radius:8px;padding:9px 11px;background:#fff;color:#154F86;font-size:10px;font-weight:900;text-decoration:none;cursor:pointer}.mou-page button:disabled{opacity:.5;cursor:not-allowed}.terms-read,.consent{display:flex;align-items:flex-start;gap:9px;padding:12px;border:1px solid #D8E3EF;border-radius:9px;background:#F5F8FC;color:#40566F;font-size:10px;line-height:1.5}.terms-read input,.consent input{margin-top:2px}.readiness-status,.execution-status{padding:7px 10px;border-radius:20px;font-size:8px}.readiness-status.ready{background:#E8F1FA;color:#165C9C}.readiness-status.pending{background:#fff1d7;color:#955a00}.execution-status{background:#EDF4FB;color:#154F86}.missing-fields{display:flex;flex-wrap:wrap;gap:7px}.missing-fields span{padding:6px 8px;border:1px solid #efd69a;border-radius:20px;background:#fff9ea;color:#795700;font-size:8px;font-weight:800}.mou-parties{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.mou-party{position:relative;text-align:center;border:1px solid #CBD9E7;border-radius:11px;padding:13px}.party-order{position:absolute;top:9px;left:9px;width:23px;height:23px;display:grid;place-items:center;border-radius:50%;background:#154F86;color:#fff;font-size:9px;font-weight:900}.mou-party h3{margin:0 0 10px;color:#154F86;font-size:12px}.signature-preview{height:82px;display:grid;place-items:center;margin-bottom:9px;border:1px dashed #B7C8D9;border-radius:7px;color:#728399;font-size:9px}.signature-preview img{max-width:90%;max-height:70px}.mou-party>b,.mou-party>small,.mou-party>em{display:block}.mou-party>b{font-size:10px}.mou-party>small{margin:4px 0 8px;color:#65778C;font-size:8px}.mou-party>em{margin-top:7px;color:#6F8094;font-size:7px;font-style:normal}.status{display:inline-block;padding:6px 9px;border-radius:20px;background:#fff1d7;color:#965b00;font-size:8px;font-weight:900}.status.signed{background:#E7F0FA;color:#165C9C}.order-lock{margin-bottom:12px;padding:11px;border:1px solid #efd69a;border-radius:8px;background:#fff9ea;color:#795700;font-size:10px;font-weight:900}.sign-grid{display:grid;grid-template-columns:1fr 1.25fr;gap:12px;margin-bottom:13px}.sign-grid label>span{display:block;margin-bottom:5px;color:#5E7187;font-size:8px;font-weight:900;text-transform:uppercase}.sign-grid input{width:100%;height:42px;padding:0 10px;border:1px solid #C8D7E6;border-radius:8px}.signature-file input{padding:8px}.signature-file small{display:block;margin-top:4px;color:#6D7E92;font-size:8px}.sign-button{margin-top:13px!important;border-color:#154F86!important;background:#154F86!important;color:#fff!important;padding:11px 16px!important}.signed-note{padding:14px;border:1px solid #B4CBE2;border-radius:9px;background:#EAF2FB;color:#165C9C}.signed-note b,.signed-note span{display:block}.signed-note span{margin:5px 0 10px;font-size:10px}.final-mou{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:19px;border-radius:13px;background:linear-gradient(135deg,#0A2747,#154F86);color:#fff}.final-mou h2{margin:5px 0;font-size:18px}.final-mou p{margin:0;color:#DFEBF7;font-size:9px}.final-mou>a{border-color:#2F73B8;background:#2F73B8;color:#fff}.final-mou button{color:#607168}@media(max-width:900px){.project-summary{grid-template-columns:repeat(2,1fr)}.sign-grid{grid-template-columns:1fr}.mou-parties{grid-template-columns:1fr 1fr}.mou-party:last-child{grid-column:1/-1}}@media(max-width:650px){.mou-header,.card-heading,.final-mou{display:block}.mou-stage{margin-top:13px}.project-summary,.mou-parties,.sign-grid{grid-template-columns:1fr}.mou-party:last-child{grid-column:auto}.card-heading>div:last-child{margin-top:12px}.final-mou>a,.final-mou>button{width:100%;margin-top:13px;text-align:center}}
`;

const STYLE = `${BASE_STYLE}
.mou-page{
  --gov-blue:#123B69;
  --gov-blue-dark:#0A2747;
  --gov-saffron:#e58a24;
  --gov-ink:#1f2933;
  --gov-muted:#617286;
  --gov-line:#9FB5CC;
  --gov-soft:#F2F6FA;
  padding:16px;
  border:1px solid #C6D2DF;
  background:#F4F7FB;
  color:var(--gov-ink);
  font-family:Arial,"Noto Sans","Segoe UI",sans-serif;
}
.gov-ribbon{
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  height:5px;
  margin:-16px -16px 0;
  border-bottom:1px solid #C6D2DF;
}
.gov-ribbon span:nth-child(1){background:var(--gov-saffron)}
.gov-ribbon span:nth-child(2){background:#fff}
.gov-ribbon span:nth-child(3){background:var(--gov-blue)}
.mou-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:20px;
  margin-top:14px;
  padding:18px 20px;
  border:1px solid var(--gov-line);
  border-top:4px solid var(--gov-blue);
  border-radius:2px;
  background:#fff;
  color:var(--gov-ink);
  box-shadow:none;
}
.gov-identity{display:flex;align-items:center;gap:16px;min-width:0}
.gov-mark{
  width:82px;
  min-width:82px;
  padding:9px 6px;
  border:1px solid var(--gov-blue);
  border-radius:1px;
  background:#F7FAFD;
  text-align:center;
}
.gov-mark strong,.gov-mark span{display:block}
.gov-mark strong{color:var(--gov-blue);font-size:15px;letter-spacing:.04em}
.gov-mark span{margin-top:4px;color:var(--gov-muted);font-size:6px;line-height:1.3;text-transform:uppercase}
.mou-header small,.card-heading small,.final-mou small{
  color:var(--gov-blue);
  font-size:8px;
  font-weight:700;
  letter-spacing:.1em;
}
.mou-header h1{margin:5px 0 4px;color:var(--gov-ink);font-size:23px;font-weight:700}
.mou-header p{margin:0;color:var(--gov-muted);font-size:10px;font-weight:600}
.mou-stage{
  min-width:245px;
  padding:10px 12px;
  border:1px solid var(--gov-line);
  border-radius:2px;
  background:var(--gov-soft);
  color:var(--gov-ink);
}
.mou-stage span{color:var(--gov-muted)}
.mou-stage b{color:var(--gov-ink)}
.mou-stage em{color:var(--gov-blue)}
.mou-message,.warning{
  border-radius:2px;
  box-shadow:none;
}
.project-summary{
  border:1px solid var(--gov-line);
  border-radius:1px;
  background:var(--gov-line);
}
.info{background:#fff}
.info span{color:var(--gov-muted);font-weight:700}
.mou-card{
  border:1px solid var(--gov-line);
  border-left:4px solid var(--gov-blue);
  border-radius:2px;
  background:#fff;
  box-shadow:none;
}
.card-heading{padding-bottom:11px;border-bottom:1px solid #D6E0EA}
.card-heading h2{color:var(--gov-ink);font-size:16px}
.card-heading p{color:var(--gov-muted)}
.mou-page button,.mou-page a{
  border:1px solid var(--gov-blue);
  border-radius:2px;
  background:#fff;
  color:var(--gov-blue-dark);
  box-shadow:none;
}
.mou-page button:hover,.mou-page a:hover{background:#EDF4FB}
.terms-read,.consent,.order-lock,.signed-note{
  border-radius:2px;
}
.readiness-status,.execution-status,.missing-fields span,.status{
  border-radius:2px;
}
.mou-party{
  border:1px solid var(--gov-line);
  border-radius:2px;
  background:#fff;
}
.party-order{
  width:24px;
  height:24px;
  border-radius:1px;
  background:var(--gov-blue);
}
.signature-preview{border-radius:1px;background:#FAFCFE}
.sign-grid input{border-radius:2px;background:#fff}
.sign-button{border-radius:2px!important;background:var(--gov-blue)!important}
.final-mou{
  border:1px solid var(--gov-blue-dark);
  border-top:4px solid var(--gov-saffron);
  border-radius:2px;
  background:var(--gov-blue);
  box-shadow:none;
}
.final-mou small{color:#DCE9F6}
.final-mou>a{border-color:#fff;background:#fff;color:var(--gov-blue-dark)}
@media(max-width:650px){
  .mou-page{padding:10px}
  .gov-ribbon{margin:-10px -10px 0}
  .gov-identity{align-items:flex-start}
  .gov-mark{width:68px;min-width:68px}
  .mou-header h1{font-size:19px}
  .mou-stage{min-width:0;width:100%}
}
`;
